import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, AlertTriangle, Info, ChevronLeft, ChevronRight, Upload, Eye, Check, Ban } from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import AttachmentPreviewModal from '../../Common/AttachmentPreviewModal';
import {
  DEFAULT_ALLOWED_UPLOAD_ACCEPT,
  validateUploadFile,
} from '../../Common/uploadValidation';
import {
  uploadFileToR2,
  createInvestmentAttachment,
  reviewInvestmentAttachment,
  deleteInvestmentAttachment,
  getInvestmentAttachmentUrl,
  getInvestmentAttachmentDownloadUrl,
} from '../../../services/proofApi';
import { payrollService, employeeService, payrollOpsService } from '../../../services/api';
import {
  getNumberRangeError,
  getPanError,
  getTanError,
  getAddressError,
  employeeRequiresPrevEmploymentDetails,
  validatePrevEmploymentFields,
  normalizePrevEmploymentForSave,
} from '../../../utils/investmentValidation';
import {
  TAX_PLANNING_TITLE,
  getCurrentFinancialYear,
  resolveTaxRegime,
  normalizeFinancialYearKey,
  financialYearsMatch,
  resolveEmployeeId,
  isJoiningFinancialYear,
  buildInvestmentSectionsPayload,
  buildTaxPlanningDeskFormData,
  financialYearFromPayMonth,
  resolveTaxPlanningPayMonth,
} from './taxPlanning';
import { usePermissions } from '../../../rbac/PermissionsContext';
import TaxLiabilityIllustration from '../payroll/TaxLiabilityIllustration';
import {
  computeSection80DAllowedFromUi,
  computeRentablePropertyBreakdown,
  computeHousePropertyAggregateFromUi,
  validateHousePropertyEntriesUi,
  computeViaDeductionsTotalOldRegime,
  computeHraExemptionDetail,
  formatInrCap,
  medicalCapForCategory,
  occupancyToUiPropertyType,
  uiPropertyTypeToOccupancy,
  isRentablePropertyType,
  MAX_SELF_OCCUPIED_PROPERTIES,
  MAX_HOUSE_PROPERTIES,
  SELF_OCCUPIED_HP_INTEREST_CAP,
  HOUSE_PROPERTY_LOSS_SETOFF_CAP,
} from '../../../utils/oldRegimeTaxCalc';

const SECTION_80C_CAP = 150000;

/** Build simple today-only open/closed status (no audience details). */
function summarizeMonthWindowToday(rawWindows) {
  const windows =
    typeof rawWindows === "string"
      ? (() => {
          try {
            return JSON.parse(rawWindows);
          } catch {
            return null;
          }
        })()
      : rawWindows;
  if (!windows || typeof windows !== "object") {
    return { openNow: false, label: "Not configured" };
  }
  const today = new Date();
  const month = today.getMonth() + 1;
  const day = today.getDate();
  const months = windows.months || {};
  const cfg = months[month] || months[String(month)] || null;
  if (!cfg || !cfg.enabled) {
    return { openNow: false, label: "Closed today" };
  }
  const fromDay = Math.max(1, Number(cfg.fromDay) || 1);
  const toDay = Math.max(fromDay, Number(cfg.toDay) || 31);
  if (day < fromDay || day > toDay) {
    return { openNow: false, label: `Closed today (open day ${fromDay}–${toDay})` };
  }
  return { openNow: true, label: `Open today (day ${fromDay}–${toDay})` };
}

// --------------------- FloatingInput Component ---------------------
const FloatingInput = ({
  id,
  label,
  value,
  onChange,
  type = 'text',
  required = false,
  disabled = false,
  error = null,
  compact = false,
  maxLength,
  rightSlot = null,
}) => {
  const handleChange = (e) => {
    const newValue = e.target.value;
    if (required && newValue === '') {
      if (onChange) onChange(e);
      return;
    }
    if (onChange) onChange(e);
  };

  const handleBlur = (e) => {
    if (required && (!e.target.value || e.target.value.trim() === '')) {
      e.target.value = '';
      if (onChange) {
        onChange({
          ...e,
          target: { ...e.target, value: '' },
        });
      }
    }
  };

  return (
    <div className={`relative ${compact ? 'mb-0' : 'mb-4'}`}>
      <div className={`flex items-start ${rightSlot ? 'gap-1' : ''}`}>
        <div className="relative min-w-0 flex-1">
          <input
            id={id}
            type={type}
            value={value || ''}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder=" "
            required={required}
            disabled={disabled}
            maxLength={maxLength}
            className={`block w-full rounded-xl border bg-white text-gray-900 appearance-none focus:outline-none focus:ring-2 focus:ring-redhalo-red/25 peer disabled:bg-gray-100 disabled:text-gray-500 ${
              compact ? 'px-2 pb-1.5 pt-3 text-xs' : 'px-3 pb-2.5 pt-4 text-sm'
            } ${
              error
                ? 'border-red-400'
                : 'border-gray-200 hover:border-redhalo-navy/40 focus:border-redhalo-red'
            }`}
          />
          <label
            htmlFor={id}
            className={`absolute ${
              compact ? 'text-[11px] left-1.5' : 'text-sm left-2'
            } ${
              error ? 'text-red-500' : 'text-gray-500'
            } duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white px-1 rounded-md 
              peer-focus:px-1 peer-focus:text-redhalo-navy 
              peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 
              peer-focus:top-2 peer-focus:scale-75 peer-focus:-translate-y-4`}
          >
            {label}
            {required && <span className="text-red-500">*</span>}
          </label>
        </div>
        {rightSlot ? (
          <div className={`flex flex-shrink-0 items-center ${compact ? 'pt-1.5' : 'pt-2.5'}`}>
            {rightSlot}
          </div>
        ) : null}
      </div>
      {error && <p className="mt-0.5 text-[10px] text-red-500">{error}</p>}
    </div>
  );
};

// --------------------- SectionUpload Component ---------------------
const SectionUpload = ({
  sectionName,
  onUpload,
  onApprove,
  onReject,
  onRemove,
  disabled = false,
  isSectionLevel = false,
  initialStatus = 'pending',
  initialRejectReason = '',
  initialAttachmentId = null,
  initialFileName,
  initialFileSize,
  onViewAttachment,
  infoText,
  /** Borderless upload icon for field right-side placement */
  variant = 'card',
}) => {
  const [file, setFile] = useState(
    initialFileName ? { name: initialFileName, size: initialFileSize || 0, type: '' } : null
  );
  const [status, setStatus] = useState(initialStatus); // pending, approved, rejected
  const [rejectReason, setRejectReason] = useState(initialRejectReason);
  const [photoModal, setPhotoModal] = useState({ isOpen: false, photo: null, fileName: '' });
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectReasonDraft, setRejectReasonDraft] = useState('');
  const [attachmentId, setAttachmentId] = useState(initialAttachmentId);
  const [uploadError, setUploadError] = useState('');
  const [showInfo, setShowInfo] = useState(false);

  // Keep internal state in sync when initial props change (after attachments load or selection changes)
  useEffect(() => {
    if (initialFileName) {
      setFile({ name: initialFileName, size: initialFileSize || 0, type: '' });
      if (initialAttachmentId) {
        setAttachmentId(initialAttachmentId);
      }
    }
  }, [initialFileName, initialFileSize, initialAttachmentId]);

  // Keep status and reject reason in sync with latest backend values
  useEffect(() => {
    setStatus(initialStatus || 'pending');
    setRejectReason(initialRejectReason || '');
  }, [initialStatus, initialRejectReason]);

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // If the same file name is selected again for this field, block and show a clear message
    if (file && selectedFile.name === file.name) {
      alert(`You already uploaded '${selectedFile.name}' for '${sectionName}'. Please choose a different file or rename it.`);
      return;
    }

    const validation = validateUploadFile(selectedFile);
    if (!validation.ok) {
      alert(`${validation.error} for '${sectionName}'.`);
      return;
    }

    setUploadError('');

    setFile(selectedFile);
    setStatus('pending');

    // If it's an image, show preview
    if (selectedFile.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoModal({
          isOpen: true,
          photo: reader.result,
          fileName: selectedFile.name,
        });
      };
      reader.readAsDataURL(selectedFile);
    }

    if (typeof onUpload === 'function') {
      try {
        const result = await onUpload({
          file: selectedFile,
          sectionName,
          isSectionLevel,
        });
        if (result && result.attachmentId) {
          setAttachmentId(result.attachmentId);
        }
      } catch (err) {
        console.error('Failed to upload proof:', err);
        setUploadError('Failed to upload proof. Please try again.');
        alert(`Failed to upload proof for '${sectionName}' (${selectedFile.name}). Please try again.`);
        setFile(null);
        setAttachmentId(null);
      }
    }
  };

  const handleViewPhoto = () => {
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoModal({ 
          isOpen: true, 
          photo: reader.result, 
          fileName: file.name 
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const closePhotoModal = () => {
    setPhotoModal({ isOpen: false, photo: null, fileName: '' });
  };

  const downloadPhoto = () => {
    if (photoModal.photo) {
      const link = document.createElement('a');
      link.href = photoModal.photo;
      link.download = photoModal.fileName || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleApprove = async () => {
    if (disabled) return;

    // Primary path: for existing attachments, always go through onApprove
    // even if local file state is null (e.g., after reload).
    if (attachmentId && typeof onApprove === 'function') {
      try {
        await onApprove({ attachmentId, sectionName });
        setStatus('approved');
      } catch (err) {
        console.error('Failed to approve attachment:', err);
        alert('Failed to approve document. Please try again.');
      }
      return;
    }

    // Backwards-compatibility: fall back to old onUpload contract if no onApprove provided
    if (typeof onUpload === 'function' && file) {
      onUpload({
        sectionName,
        fileName: file.name,
        size: file.size,
        status: 'approved',
        lastUpdated: new Date().toISOString(),
        isSectionLevel,
      });
      setStatus('approved');
    }
  };

  const handleReject = () => {
    if (disabled || !attachmentId) return;
    setRejectReasonDraft(rejectReason || '');
    setIsRejectModalOpen(true);
  };

  // Prefer the real uploaded filename when we have it; otherwise fall back to
  // the logical section label so the UI never shows an empty name.
  const displayName = file?.name || initialFileName || sectionName;
  const displaySizeBytes =
    file?.size != null ? file.size : initialFileSize != null ? initialFileSize : 0;
  const inputId = `file-upload-${String(sectionName || 'field').replace(/[^a-zA-Z0-9_-]/g, '-')}`;
  const hasProof = !!(file || attachmentId);
  const uploadTitle = hasProof
    ? `Replace proof (${displayName})`
    : `Upload proof for ${sectionName}`;

  const inlineActions = (
    <div className="inline-flex items-center gap-0.5">
      <div className="relative">
        <input
          type="file"
          accept={DEFAULT_ALLOWED_UPLOAD_ACCEPT}
          onChange={handleFileChange}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          id={inputId}
          disabled={disabled}
          title={uploadTitle}
        />
        <label
          htmlFor={inputId}
          className={`inline-flex h-8 w-8 items-center justify-center ${
            disabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'
          } ${
            status === 'rejected'
              ? 'text-red-500'
              : hasProof
                ? 'text-emerald-600 hover:text-emerald-700'
                : 'text-slate-500 hover:text-redhalo-navy'
          }`}
          title={uploadTitle}
        >
          <Upload className="h-4 w-4" strokeWidth={2} />
        </label>
      </div>
      {attachmentId && status !== 'rejected' && typeof onViewAttachment === 'function' && (
        <button
          type="button"
          onClick={() =>
            onViewAttachment(attachmentId, file?.name || initialFileName || sectionName)
          }
          className="inline-flex h-8 w-8 items-center justify-center text-slate-500 hover:text-redhalo-navy"
          title={`View ${displayName}`}
          disabled={disabled}
        >
          <Eye className="h-4 w-4" strokeWidth={2} />
        </button>
      )}
      {file && !attachmentId && status !== 'rejected' && file.type?.startsWith?.('image/') && (
        <button
          type="button"
          onClick={handleViewPhoto}
          className="inline-flex h-8 w-8 items-center justify-center text-slate-500 hover:text-redhalo-navy"
          title="Preview"
        >
          <Eye className="h-4 w-4" strokeWidth={2} />
        </button>
      )}
      {(file || attachmentId) && (
        <>
          <button
            type="button"
            onClick={handleApprove}
            disabled={disabled || status === 'approved' || !attachmentId}
            className={`inline-flex h-8 w-8 items-center justify-center ${
              status === 'approved'
                ? 'text-emerald-600'
                : 'text-slate-400 hover:text-emerald-600 disabled:opacity-30'
            }`}
            title="Approve"
          >
            <Check className="h-4 w-4" strokeWidth={2.25} />
          </button>
          <button
            type="button"
            onClick={handleReject}
            disabled={disabled || status === 'rejected' || !attachmentId}
            className={`inline-flex h-8 w-8 items-center justify-center ${
              status === 'rejected'
                ? 'text-red-600'
                : 'text-slate-400 hover:text-red-600 disabled:opacity-30'
            }`}
            title="Reject"
          >
            <Ban className="h-3.5 w-3.5" strokeWidth={2.25} />
          </button>
        </>
      )}
    </div>
  );

  if (variant === 'inline') {
    return (
      <div className="relative inline-flex flex-col items-end">
        {inlineActions}
        {uploadError ? (
          <p className="mt-0.5 max-w-[8rem] text-[10px] leading-tight text-red-600">{uploadError}</p>
        ) : null}
        {status === 'rejected' && rejectReason ? (
          <p className="mt-0.5 max-w-[8rem] truncate text-[10px] text-red-600" title={rejectReason}>
            Rejected
          </p>
        ) : null}

        {isRejectModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="w-full max-w-md bg-white p-6">
              <div className="mb-4 flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Reject Document</h3>
                  <p className="mt-1 text-xs text-gray-600">
                    {sectionName}
                    {file?.name ? ` - ${file.name}` : ''}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsRejectModalOpen(false)}
                  className="ml-4 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="mb-4">
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Reason for rejection
                </label>
                <textarea
                  value={rejectReasonDraft}
                  onChange={(e) => setRejectReasonDraft(e.target.value)}
                  rows="3"
                  className="block w-full border border-gray-300 bg-transparent px-2.5 pb-2.5 pt-2.5 text-sm text-gray-900 focus:border-redhalo-navy focus:outline-none focus:ring-0"
                />
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setIsRejectModalOpen(false)}
                  className="bg-gray-200 px-4 py-2 text-sm text-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    const trimmed = rejectReasonDraft.trim();
                    if (!trimmed) return;
                    setRejectReason(trimmed);
                    if (attachmentId && typeof onReject === 'function') {
                      try {
                        await onReject({ attachmentId, reason: trimmed, sectionName });
                        setStatus('rejected');
                        setIsRejectModalOpen(false);
                      } catch (err) {
                        console.error('Failed to reject attachment:', err);
                        alert('Failed to reject document. Please try again.');
                      }
                      return;
                    }
                    if (typeof onUpload === 'function' && file) {
                      onUpload({
                        sectionName,
                        fileName: file.name,
                        size: file.size,
                        status: 'rejected',
                        rejectReason: trimmed,
                        lastUpdated: new Date().toISOString(),
                        isSectionLevel,
                      });
                      setStatus('rejected');
                      setIsRejectModalOpen(false);
                    }
                  }}
                  className="bg-[hsl(var(--ds-primary))] px-4 py-2 text-sm text-[hsl(var(--ds-primary-fg))] hover:bg-[hsl(var(--ds-primary-hover))]"
                >
                  Reject
                </button>
              </div>
            </div>
          </div>
        )}

        {photoModal.isOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="max-h-[90vh] max-w-2xl overflow-auto bg-white p-6">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-semibold">{photoModal.fileName || 'Preview'}</h3>
                <button type="button" onClick={closePhotoModal} className="text-gray-500">
                  <X className="h-4 w-4" />
                </button>
              </div>
              {photoModal.photo ? (
                <img src={photoModal.photo} alt="Preview" className="max-w-full" />
              ) : null}
              <div className="mt-3 flex justify-end gap-2">
                <button type="button" onClick={downloadPhoto} className="text-sm text-redhalo-navy">
                  Download
                </button>
                <button type="button" onClick={closePhotoModal} className="text-sm">
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mt-2 p-2 border border-gray-200 bg-gray-50 relative">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center space-x-2">
          {/* Photo Preview/Display for newly selected images (not shown once rejected).
              When an attachmentId already exists, rely on the backend view icon instead
              of showing a second local preview eye icon. */}
          {file && !attachmentId && status !== 'rejected' && file.type.startsWith('image/') && (
            <div className="mr-2">
              <div
                className="w-8 h-8 bg-redhalo-mint/60 flex items-center justify-center cursor-pointer"
                onClick={handleViewPhoto}
              >
                <img
                  src={photoModal.photo || ''}
                  alt="Document Preview"
                  className="w-6 h-6 object-cover rounded"
                />
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-1">
            {/* Local preview for newly selected images (before upload or for quick check).
                When attachmentId exists, use the backend-driven view icon instead of this one. */}
            {file && !attachmentId && status !== 'rejected' && file.type.startsWith('image/') && (
              <button
                type="button"
                onClick={handleViewPhoto}
                className="w-6 h-6 text-redhalo-navy bg-redhalo-mint/40 border border-redhalo-teal/50 rounded hover:bg-redhalo-mint/60 flex items-center justify-center"
                title="View Document"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </button>
            )}

            {/* Upload / replace document (always first action icon) */}
            <div className="relative">
              <input
                type="file"
                accept={DEFAULT_ALLOWED_UPLOAD_ACCEPT}
                onChange={handleFileChange}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                id={inputId}
                disabled={disabled}
              />
              <label
                htmlFor={inputId}
                className={`inline-flex h-6 w-6 items-center justify-center text-slate-500 hover:text-redhalo-navy cursor-pointer ${
                  disabled ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                title={file ? "Replace Document" : "Upload Document"}
              >
                <Upload className="h-3.5 w-3.5" strokeWidth={2} />
              </label>
            </div>

            {/* View existing attachment via backend (second action icon). Hidden once rejected. */}
            {attachmentId && status !== 'rejected' && typeof onViewAttachment === 'function' && (
              <button
                type="button"
                onClick={() => onViewAttachment(attachmentId, file?.name || initialFileName || sectionName)}
                className="w-6 h-6 text-redhalo-navy bg-redhalo-mint/40 border border-redhalo-teal/50 rounded hover:bg-redhalo-mint/60 flex items-center justify-center"
                title="View Document"
                disabled={disabled}
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </button>
            )}
            
            {/* Approve/Reject Buttons.
                Show for existing attachments even if local file state is null, so that
                admin can always approve/reject a stored proof. */}
            {(file || attachmentId) && (
              <div className="flex space-x-1">
                <button
                  onClick={handleApprove}
                  disabled={disabled || status === 'approved' || !attachmentId}
                  className={`w-6 h-6 rounded flex items-center justify-center ${
                    status === 'approved' 
                      ? 'bg-green-100 text-green-600 border border-green-200' 
                      : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-green-50 hover:text-green-600'
                  }`}
                  title="Approve"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </button>
                <button
                  onClick={handleReject}
                  disabled={disabled || status === 'rejected' || !attachmentId}
                  className={`w-6 h-6 rounded flex items-center justify-center ${
                    status === 'rejected' 
                      ? 'bg-red-100 text-red-600 border border-red-200' 
                      : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-red-50 hover:text-red-600'
                  }`}
                  title="Reject"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
          </div>
          {infoText && (
            <div className="relative ml-1">
              <button
                type="button"
                className="w-6 h-6 text-gray-600 bg-gray-100 border border-gray-200 rounded flex items-center justify-center hover:bg-gray-200"
                title="More information"
                onClick={() => setShowInfo((prev) => !prev)}
                disabled={disabled}
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6a9 9 0 110 18 9 9 0 010-18z" />
                </svg>
              </button>
              {showInfo && (
                <div className="absolute z-20 mt-1 w-64 max-w-xs p-2 bg-white border border-gray-300 shadow-lg text-xs text-gray-700 rounded-none">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-semibold truncate mr-2">{sectionName}</span>
                    <button
                      type="button"
                      className="text-gray-500 hover:text-gray-800"
                      onClick={() => setShowInfo(false)}
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <div className="whitespace-pre-line break-words">{infoText}</div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Middle: filename/size when pending/approved, or reason when rejected */}
        <div className="flex-1 min-w-0">
          {status === 'rejected' && rejectReason ? (
            <div
              className="text-xs text-red-700 truncate text-center md:text-left"
              title={rejectReason}
            >
              Reason: {rejectReason}
            </div>
          ) : (
            displayName && status !== 'rejected' && (
              <div className="text-xs text-gray-600 truncate text-center md:text-left">
                {displayName}
                {displaySizeBytes
                  ? ` (${(displaySizeBytes / 1024 / 1024).toFixed(2)} MB)`
                  : ''}
              </div>
            )
          )}
        </div>

        {/* Right: status pill only */}
        {status && status !== 'pending' ? (
          <div className="ml-2 flex flex-col items-end max-w-xs">
            <span
              className={`text-xs px-2 py-1 rounded-full ${
                status === 'approved'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}
            >
              {status === 'approved' ? 'Approved' : 'Rejected'}
            </span>
          </div>
        ) : null}
      </div>
      {uploadError && (
        <div className="mt-2 text-xs text-red-600 bg-red-50 border border-red-200 px-2 py-1">
          {uploadError}
        </div>
      )}

      {isRejectModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 w-full max-w-md">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Reject Document</h3>
                <p className="text-xs text-gray-600 mt-1">
                  {sectionName}
                  {file?.name ? ` - ${file.name}` : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsRejectModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 ml-4"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Reason for rejection</label>
              <textarea
                value={rejectReasonDraft}
                onChange={(e) => setRejectReasonDraft(e.target.value)}
                rows="3"
                className="block w-full text-sm text-gray-900 bg-transparent border border-gray-300 px-2.5 pb-2.5 pt-2.5 focus:outline-none focus:ring-0 focus:border-redhalo-navy"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={() => setIsRejectModalOpen(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  const trimmed = rejectReasonDraft.trim();
                  // Require a reason, but do not require a local file object for
                  // existing attachments (after reload/tab change we only
                  // have attachmentId & callbacks).
                  if (!trimmed) return;
                  setRejectReason(trimmed);

                  if (attachmentId && typeof onReject === 'function') {
                    try {
                      await onReject({ attachmentId, reason: trimmed, sectionName });
                      setStatus('rejected');
                      setIsRejectModalOpen(false);
                    } catch (err) {
                      console.error('Failed to reject attachment:', err);
                      alert('Failed to reject document. Please try again.');
                    }
                    return;
                  }

                  // Backwards-compatibility: fall back to old onUpload contract if no onReject provided
                  if (typeof onUpload === 'function' && file) {
                    onUpload({
                      sectionName,
                      fileName: file.name,
                      size: file.size,
                      status: 'rejected',
                      rejectReason: trimmed,
                      lastUpdated: new Date().toISOString(),
                      isSectionLevel,
                    });
                    setStatus('rejected');
                    setIsRejectModalOpen(false);
                  }
                }}
                className="px-4 py-2 bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))] text-sm hover:bg-[hsl(var(--ds-primary-hover))]"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Photo Modal */}
      {photoModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 max-w-2xl max-h-[90vh] overflow-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {photoModal.fileName || 'Document Preview'}
              </h3>
              <button
                onClick={closePhotoModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="flex justify-center mb-4">
              {photoModal.photo ? (
                <img
                  src={photoModal.photo}
                  alt="Document Preview"
                  className="max-w-full max-h-96 object-contain"
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik01MCAxMDAgQzUwIDcyLjM4NTggNzIuMzg1OCA1MCAxMDAgNTAgQzEyNy42MTQgNTAgMTUwIDcyLjM4NTggMTUwIDEwMCBDMTUwIDEyNy42MTQgMTI3LjYxNCAxNTAgMTAwIDE1MCBDNzIuMzg1OCAxNTAgNTAgMTI3LjYxNCA1MCAxMDBaIiBmaWxsPSIjOUI5QkEwIi8+CjxwYXRoIGQ9Ik0xMDAgMTMwIEM4OS4wNTQ0IDEzMCA4MCAxMjAuOTQ1NiA4MCAxMTAgQzgwIDk5LjA1NDQgODkuMDU0NCA5MCAxMDAgOTAgQzExMC45NDYgOTAgMTIwIDk5LjA1NDQgMTIwIDExMCBDMTIwIDEyMC45NDU2IDExMC45NDYgMTMwIDEwMCAxMzBaIiBmaWxsPSIjOUI5QkEwIi8+CjxwYXRoIGQ9Ik0xMDAgMTQwIEM4OS4wNTQ0IDE0MCA4MCAxMzAuOTQ1NiA4MCAxMjAgQzgwIDEwOS4wNTQ0IDg5LjA1NDQgMTAwIDEwMCAxMDAgQzExMC45NDYgMTAwIDEyMCAxMDkuMDU0NCAxMjAgMTIwIEMxMjAgMTMwLjk0NTYgMTEwLjk0NiAxNDAgMTAwIDE0MFoiIGZpbGw9IiM5QjlCQTAiLz4KPC9zdmc+';
                  }}
                />
              ) : (
                <div className="w-48 h-48 bg-gray-100 flex items-center justify-center">
                  <svg className="w-24 h-24 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
              )}
            </div>
            
            <div className="flex justify-center space-x-4">
              {photoModal.photo && (
                <button
                  onClick={downloadPhoto}
                  className="px-4 py-2 rh-btn-primary rounded-md hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-redhalo-red/35 focus:ring-offset-2 flex items-center space-x-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>Download</span>
                </button>
              )}
              <button
                onClick={closePhotoModal}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// --------------------- BulkActions Component ---------------------
const BulkActions = ({ sectionName, onBulkAction, disabled = false }) => {
  const handleBulkApprove = () => {
    onBulkAction(sectionName, 'approve');
  };

  const handleBulkReject = () => {
    const reason = prompt('Enter reason for rejection:');
    if (reason) {
      onBulkAction(sectionName, 'reject', reason);
    }
  };

  return (
    <div className="mt-2 p-2 border border-gray-200 bg-redhalo-mint/40">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-gray-700">Bulk Actions:</span>
        <div className="flex items-center space-x-2">
          <button
            onClick={handleBulkApprove}
            disabled={disabled}
            className="px-2 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 disabled:bg-gray-400"
          >
            ✓ Approve All
          </button>
          <button
            onClick={handleBulkReject}
            disabled={disabled}
            className="px-2 py-1 bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))] text-xs rounded hover:bg-[hsl(var(--ds-primary-hover))] disabled:bg-gray-400"
          >
            ✗ Reject All
          </button>
        </div>
      </div>
    </div>
  );
};

// --------------------- TaxPlanningModal ---------------------
export default function TaxPlanningModal({
  employee,
  mode,
  onClose,
  onSave,
  declarationHeader,
  attachments,
  payrollSettings,
  isInvestmentDeclarationEnabled,
  isInvestmentProofEnabled,
  getInvestmentDeclarationMode,
  getInvestmentProofMode,
  financialYear,
  /** Run Payroll / payroll month context — keeps TDS & liability on the same FY as process. */
  payMonth = null,
  /** Payroll Actions: admin may edit outside Declaration windows (FY lock still applies). */
  adminBypassWindows = false,
  historySlot = null,
}) {
  console.debug('🔍 TaxPlanningModal rendered with:', {
    employee: employee?.id,
    mode,
    financialYear,
    payMonth,
    payrollSettings: payrollSettings ? 'present' : 'missing'
  });
  const { isAdmin, role } = usePermissions();
  const isAdminOrHr =
    !!adminBypassWindows ||
    !!isAdmin ||
    String(role || "").toLowerCase() === "hr";

  const companyDefaultRegime = resolveTaxRegime(
    payrollSettings?.defaultTaxRegime || "NEW"
  );

  const [activeTab, setActiveTab] = useState('hra');
  const [illustrationRefreshKey, setIllustrationRefreshKey] = useState(0);
  const [selectedTaxRegime, setSelectedTaxRegime] = useState(() =>
    resolveTaxRegime(
      declarationHeader?.taxRegime ||
        employee?.taxRegime ||
        payrollSettings?.defaultTaxRegime ||
        "NEW"
    )
  );
  const [isTaxRegimeSwitchLocked, setIsTaxRegimeSwitchLocked] = useState(
    !!declarationHeader?.isTaxRegimeSwitchLocked
  );
  const [isPrevEmploymentSubmitted, setIsPrevEmploymentSubmitted] = useState(
    !!declarationHeader?.isPrevEmploymentSubmitted
  );
  const [showAccessInfo, setShowAccessInfo] = useState(false);
  const accessInfoRef = useRef(null);
  const [showHpAggInfo, setShowHpAggInfo] = useState(false);
  const hpAggInfoRef = useRef(null);
  const [show80DInfo, setShow80DInfo] = useState(false);
  const section80DInfoRef = useRef(null);
  const [showHraInfo, setShowHraInfo] = useState(false);
  const hraInfoRef = useRef(null);
  const [show80CInfo, setShow80CInfo] = useState(false);
  const section80CInfoRef = useRef(null);
  const [salaryTaxBases, setSalaryTaxBases] = useState({
    basicAnnual: 0,
    hraAnnual: 0,
    loading: false,
    loaded: false,
  });
  const [employeeProfile, setEmployeeProfile] = useState(null);
  const [declarationData, setDeclarationData] = useState({
    houseRent: {
      cityType: '',
      rentPaid: '',
      periods: [],
      landlordPan: '',
      address: '',
    },
    hraEntries: [],
    section80C: {
      lifeInsurance: '',
      ppf: '',
      vpf: '',
      pensionFund: '',
      nsc: '',
      nscInterest: '',
      ulip: '',
      elss: '',
      tuitionFees: '',
      houseLoanPrincipal: '',
      registrationCharges: '',
      sukanyaSamriddhi: '',
      infrastructureBonds: '',
      fixedDeposit: '',
    },
    sectionVIA: {
      section80D: {
        self: '',
        parents: '',
        isSelfSeniorCitizen: false,
        isParentsSeniorCitizen: false,
        selfPreventiveHealthCheckup: '',
        parentsPreventiveHealthCheckup: '',
      },
      otherInvestments: {
        section80E: '',
        section80CCD1B: '',
        section80EE: '',
        section80EEA: '',
        section80EEB: '',
        section80TTA: '',
      },
    },
    houseProperty: {
      propertyType: '',
      loanTakenBeforeApril1999: false,
      interest: '',
      rentalIncome: '',
      address: '',
    },
    housePropertyEntries: [],
  });

  const getEffectiveFinancialYear = () => {
    // Payroll process context wins so Tax Planning / TDS stay identical to the pay month.
    const fromPay = financialYearFromPayMonth(payMonth);
    if (fromPay) return fromPay;
    return (
      financialYear ||
      declarationHeader?.financialYear ||
      getCurrentFinancialYear()
    );
  };

  // Previous Employment header fields
  const [prevEmployment, setPrevEmployment] = useState({
    employerName: declarationHeader?.prevEmployerName?.toString() || '',
    employerPan: declarationHeader?.prevEmployerPan?.toString() || '',
    employerTan: declarationHeader?.prevEmployerTan?.toString() || '',
    grossSalary:
      declarationHeader?.prevGrossSalary != null
        ? declarationHeader.prevGrossSalary.toString()
        : '',
    taxDeducted:
      declarationHeader?.prevTaxDeducted != null
        ? declarationHeader.prevTaxDeducted.toString()
        : '',
    professionalTax:
      declarationHeader?.prevProfessionalTax != null
        ? declarationHeader.prevProfessionalTax.toString()
        : '',
  });

  useEffect(() => {
    if (!declarationHeader) return;
    setPrevEmployment({
      employerName: declarationHeader.prevEmployerName?.toString() || '',
      employerPan: declarationHeader.prevEmployerPan?.toString() || '',
      employerTan: declarationHeader.prevEmployerTan?.toString() || '',
      grossSalary:
        declarationHeader.prevGrossSalary != null
          ? declarationHeader.prevGrossSalary.toString()
          : '',
      taxDeducted:
        declarationHeader.prevTaxDeducted != null
          ? declarationHeader.prevTaxDeducted.toString()
          : '',
      professionalTax:
        declarationHeader.prevProfessionalTax != null
          ? declarationHeader.prevProfessionalTax.toString()
          : '',
    });
    const overrideState = {
      allowAllEdits: !!declarationHeader.allowAllEdits,
      allowPrevEmploymentEdit: !!declarationHeader.allowPrevEmploymentEdit,
      allowHraEdit: !!declarationHeader.allowHraEdit,
      allowSection80CEdit: !!declarationHeader.allowSection80CEdit,
      allowViaEdit: !!declarationHeader.allowViaEdit,
      allowHousePropertyEdit: !!declarationHeader.allowHousePropertyEdit,
    };
    setAllowAllEdits(overrideState.allowAllEdits);
    setAllowPrevEmploymentEdit(overrideState.allowPrevEmploymentEdit);
    setAllowHraEdit(overrideState.allowHraEdit);
    setAllowSection80CEdit(overrideState.allowSection80CEdit);
    setAllowViaEdit(overrideState.allowViaEdit);
    setAllowHousePropertyEdit(overrideState.allowHousePropertyEdit);
    // Also update the original state
    setOriginalOverrideState(overrideState);
  }, [
    declarationHeader?.prevEmployerName,
    declarationHeader?.prevEmployerPan,
    declarationHeader?.prevEmployerTan,
    declarationHeader?.prevGrossSalary,
    declarationHeader?.prevTaxDeducted,
    declarationHeader?.prevProfessionalTax,
    declarationHeader?.allowAllEdits,
    declarationHeader?.allowPrevEmploymentEdit,
    declarationHeader?.allowHraEdit,
    declarationHeader?.allowSection80CEdit,
    declarationHeader?.allowViaEdit,
    declarationHeader?.allowHousePropertyEdit,
  ]);

  const [errors, setErrors] = useState({});
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [showSubmitSuccessModal, setShowSubmitSuccessModal] = useState(false);
  const [submitConfirm, setSubmitConfirm] = useState({ open: false, mode: null }); // mode: 'old' | 'new'
  const [cancelConfirmMode, setCancelConfirmMode] = useState(null); // 'old_nothing_declared' | 'new' | null
  const initialSelectedTaxRegimeRef = useRef(null); // regime when modal was loaded; cancel popup only when user changed regime (old↔new)
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [savedSections, setSavedSections] = useState(new Set());
  const darkMode = false;

  // Admin override flags to reopen sections for editing in employee view
  const [allowAllEdits, setAllowAllEdits] = useState(!!declarationHeader?.allowAllEdits);
  const [allowPrevEmploymentEdit, setAllowPrevEmploymentEdit] = useState(
    !!declarationHeader?.allowPrevEmploymentEdit
  );
  const [allowHraEdit, setAllowHraEdit] = useState(!!declarationHeader?.allowHraEdit);
  const [allowSection80CEdit, setAllowSection80CEdit] = useState(
    !!declarationHeader?.allowSection80CEdit
  );
  const [allowViaEdit, setAllowViaEdit] = useState(!!declarationHeader?.allowViaEdit);
  const [allowHousePropertyEdit, setAllowHousePropertyEdit] = useState(
    !!declarationHeader?.allowHousePropertyEdit
  );

  // Original override state - tracks the state that was last saved/loaded from backend
  // This is what gets used when saving form sections (HRA, etc.)
  const [originalOverrideState, setOriginalOverrideState] = useState({
    allowAllEdits: !!declarationHeader?.allowAllEdits,
    allowPrevEmploymentEdit: !!declarationHeader?.allowPrevEmploymentEdit,
    allowHraEdit: !!declarationHeader?.allowHraEdit,
    allowSection80CEdit: !!declarationHeader?.allowSection80CEdit,
    allowViaEdit: !!declarationHeader?.allowViaEdit,
    allowHousePropertyEdit: !!declarationHeader?.allowHousePropertyEdit,
  });

  // When allowAllEdits is turned on, auto-enable all section flags so
  // the UI reflects that everything is open. When turned off, leave
  // section flags as-is so admin can fine-tune.
  useEffect(() => {
    if (allowAllEdits) {
      setAllowPrevEmploymentEdit(true);
      setAllowHraEdit(true);
      setAllowSection80CEdit(true);
      setAllowViaEdit(true);
      setAllowHousePropertyEdit(true);
    }
  }, [allowAllEdits]);

  const setFieldError = (key, message) => {
    setErrors((prev) => {
      const next = { ...prev };
      if (message) next[key] = message;
      else delete next[key];
      return next;
    });
  };

  const hasProof = (sectionName) => {
    const att = getFieldAttachment(sectionName);
    return !!(att && att.attachmentId);
  };

  const getPrevEmploymentExperiences = () =>
    employeeProfile?.experiences ?? employee?.experiences ?? [];

  const getPrevEmploymentIsFresher = () =>
    employeeProfile?.isFresher ?? employee?.isFresher;

  const requiresPrevEmploymentDetails = () =>
    employeeRequiresPrevEmploymentDetails({
      experiences: getPrevEmploymentExperiences(),
      isFresher: getPrevEmploymentIsFresher(),
    });

  const buildPrevEmploymentHeaderPart = () =>
    normalizePrevEmploymentForSave(prevEmployment, {
      applyDefaultsIfEmpty: !requiresPrevEmploymentDetails(),
    });

  const validateCurrentSection = (sectionId) => {
    const nextErrors = {};

    if (sectionId === 'prevEmployment') {
      Object.assign(
        nextErrors,
        validatePrevEmploymentFields(prevEmployment, {
          required: requiresPrevEmploymentDetails(),
        })
      );
    }

    if (sectionId === 'hra') {
      const rows = declarationData.hraEntries || [];
      for (let i = 0; i < rows.length; i++) {
        const rentKey = `hra-rentPaid-${i}`;
        const panKey = `hra-landlordPan-${i}`;
        const addrKey = `hra-address-${i}`;
        const rentErr = getNumberRangeError(`Total Rent Paid `, rows[i]?.totalRentPaid);
        const panErr = getPanError(`Landlord PAN`, rows[i]?.landlordPan);
        const addrErr = getAddressError(`Rental Address `, rows[i]?.rentalAddress);
        if (rentErr) nextErrors[rentKey] = rentErr;
        if (panErr) nextErrors[panKey] = panErr;
        if (addrErr) nextErrors[addrKey] = addrErr;

        const amount = Number(rows[i]?.totalRentPaid) || 0;
        
        // If total rent > 1 lakh (100000), landlord PAN is mandatory
        if (amount > 100000) {
          const landlordPan = (rows[i]?.landlordPan || '').trim();
          if (!landlordPan) {
            nextErrors[panKey] = `Landlord PAN is mandatory when Total Rent Paid is more than ₹1,00,000`;
          } else {
            // Still validate PAN format if provided
            const panValidationErr = getPanError(`Landlord PAN`, landlordPan);
            if (panValidationErr) {
              nextErrors[panKey] = panValidationErr;
            }
          }
        }
        
        // Only show proof document error if proof upload is enabled and not expired/hidden
        if (amount > 0 && !hasProof(`HRA - Entry ${i + 1}`) && isInvestmentProofEnabled && !isProofUploadDisabled()) {
          nextErrors[rentKey] =
            nextErrors[rentKey] ||
            'Proof document is required when Total Rent Paid/Payable is entered.';
        }
      }
    }

    if (sectionId === 'section80C') {
      const s = declarationData.section80C || {};
      const map = [
        [
          'lifeInsurance',
          'Life Insurance',
          s.lifeInsurance,
          'Life Insurance Premium',
        ],
        [
          'ppf',
          'PPF',
          s.ppf,
          'Public Provident Fund (PPF)',
        ],
        [
          'vpf',
          'VPF',
          s.vpf,
          'Voluntary Provident Fund (VPF)',
        ],
        [
          'pensionFund',
          'Pension Fund',
          s.pensionFund,
          'Pension Fund Contribution',
        ],
        [
          'nsc',
          'NSC',
          s.nsc,
          'National Savings Certificate (NSC)',
        ],
        [
          'nscInterest',
          'NSC Interest',
          s.nscInterest,
          'Interest Accrued on NSC (Reinvested)',
        ],
        [
          'ulip',
          'ULIP',
          s.ulip,
          'Unit Linked Insurance Policy (ULIP)',
        ],
        [
          'elss',
          'ELSS',
          s.elss,
          'Equity Linked Savings Schemes (ELSS)',
        ],
        [
          'tuitionFees',
          'Tuition Fees',
          s.tuitionFees,
          'Payment of Tuition Fees (Max 2 Children)',
        ],
        [
          'houseLoanPrincipal',
          'House Loan Principal',
          s.houseLoanPrincipal,
          'Principal Repayment of Housing Loan',
        ],
        [
          'registrationCharges',
          'Registration Charges',
          s.registrationCharges,
          'Registration Charges for Buying House',
        ],
        [
          'sukanyaSamriddhi',
          'Sukanya Samriddhi',
          s.sukanyaSamriddhi,
          'Sukanya Samriddhi Yojana',
        ],
        [
          'infrastructureBonds',
          'Infrastructure Bonds',
          s.infrastructureBonds,
          'Infrastructure Bonds',
        ],
        [
          'fixedDeposit',
          'Fixed Deposit',
          s.fixedDeposit,
          'Bank Fixed Deposit (5+ Years)',
        ],
      ];
      for (const [key, label, value, proofLabel] of map) {
        const err = getNumberRangeError(label, value);
        if (err) nextErrors[key] = err;

        const amount = Number(value) || 0;
        // Only show proof document error if proof upload is enabled and not expired/hidden
        if (amount > 0 && !hasProof(proofLabel) && isInvestmentProofEnabled && !isProofUploadDisabled()) {
          nextErrors[key] =
            nextErrors[key] ||
            `Proof document is required when ${label} is entered.`;
        }
      }
    }

    if (sectionId === 'viaDeductions') {
      const d = declarationData.sectionVIA?.section80D || {};
      const o = declarationData.sectionVIA?.otherInvestments || {};
      const map = [
        [
          'section80DSelf',
          'Self Medical Insurance',
          d.self,
          'Section 80D - Medical Insurance',
        ],
        [
          'section80DParents',
          'Parents Medical Insurance',
          d.parents,
          'Section 80D - Medical Insurance',
        ],
        [
          'selfPreventiveHealthCheckup',
          'Self Preventive Health Checkup',
          d.selfPreventiveHealthCheckup,
          'Section 80D - Medical Insurance',
        ],
        [
          'parentsPreventiveHealthCheckup',
          'Parents Preventive Health Checkup',
          d.parentsPreventiveHealthCheckup,
          'Section 80D - Medical Insurance',
        ],
        [
          'section80E',
          'Interest Education Loan (80E)',
          o.section80E,
          'Interest on Education Loan (80E)',
        ],
        [
          'section80CCD1B',
          'NPS Additional Contribution (80CCD(1B))',
          o.section80CCD1B,
          'NPS Additional Contribution (80CCD(1B))',
        ],
        [
          'section80EE',
          'Interest on Home Loan (80EE)',
          o.section80EE,
          'Interest on Home Loan (80EE)',
        ],
        [
          'section80EEA',
          'Interest on Home Loan (80EEA)',
          o.section80EEA,
          'Interest on Home Loan (80EEA)',
        ],
        [
          'section80EEB',
          'Interest on Home Loan (80EEB)',
          o.section80EEB,
          'Interest on Home Loan (80EEB)',
        ],
        [
          'section80TTA',
          'Interest on Savings (80TTA)',
          o.section80TTA,
          'Interest on Savings Account (80TTA)',
        ],
      ];
      for (const [key, label, value, proofLabel] of map) {
        const err = getNumberRangeError(label, value);
        if (err) nextErrors[key] = err;

        const amount = Number(value) || 0;
        // Only show proof document error if proof upload is enabled and not expired/hidden
        if (amount > 0 && !hasProof(proofLabel) && isInvestmentProofEnabled && !isProofUploadDisabled()) {
          nextErrors[key] =
            nextErrors[key] ||
            `Proof document is required when ${label} is entered.`;
        }
      }
    }

    if (sectionId === 'houseProperty') {
      const rows = declarationData.housePropertyEntries || [];
      for (let i = 0; i < rows.length; i++) {
        const key = `interest-${i}`;
        const err = getNumberRangeError(
          `Interest Paid on Borrowed Capital `,
          rows[i]?.interest
        );
        if (err) nextErrors[key] = err;

        if (rows[i]?.propertyType === 'let-out' || rows[i]?.propertyType === 'deemed-let-out') {
          const gavKey = `grossAnnualValue-${i}`;
          const gavErr = getNumberRangeError(
            'Gross Annual Value',
            rows[i]?.grossAnnualValue
          );
          if (gavErr) nextErrors[gavKey] = gavErr;
          if (!rows[i]?.grossAnnualValue && rows[i]?.grossAnnualValue !== 0) {
            nextErrors[gavKey] = nextErrors[gavKey] || 'Gross Annual Value is required for let-out property';
          }
          const munKey = `municipalTaxes-${i}`;
          const munErr = getNumberRangeError(
            'Municipal taxes',
            rows[i]?.municipalTaxes
          );
          if (munErr) nextErrors[munKey] = munErr;
        }

        const amount = Number(rows[i]?.interest) || 0;
        // Only show proof document error if proof upload is enabled and not expired/hidden
        if (amount > 0 && !hasProof(`House Property - Entry ${i + 1}`) && isInvestmentProofEnabled && !isProofUploadDisabled()) {
          nextErrors[key] =
            nextErrors[key] ||
            'Proof document is required when Interest Paid on Borrowed Capital is entered.';
        }
      }
      const hpValidation = validateHousePropertyEntriesUi(rows);
      if (!hpValidation.valid) {
        nextErrors.housePropertyAggregate = hpValidation.reason;
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  // Global attachment preview state (reuse employee behavior)
  const [attachmentPreview, setAttachmentPreview] = useState({
    isOpen: false,
    url: null,
    downloadUrl: null,
    fileName: '',
  });

  const attachmentUrlCacheRef = useRef(new Map());
  const attachmentDownloadUrlCacheRef = useRef(new Map());

  const openAttachment = async (attachmentId, fileName) => {
    if (!attachmentId) return;
    try {
      const cached = attachmentUrlCacheRef.current.get(attachmentId);
      const cachedDownload = attachmentDownloadUrlCacheRef.current.get(attachmentId);
      if (cached) {
        setAttachmentPreview({
          isOpen: true,
          url: cached,
          downloadUrl: cachedDownload || null,
          fileName: fileName || 'Document',
        });
        return;
      }

      const [previewResp, downloadResp] = await Promise.all([
        getInvestmentAttachmentUrl(attachmentId),
        getInvestmentAttachmentDownloadUrl(attachmentId),
      ]);
      const url = previewResp?.data?.url || previewResp?.url || previewResp;
      const downloadUrl =
        downloadResp?.data?.url || downloadResp?.url || downloadResp;
      if (url) {
        attachmentUrlCacheRef.current.set(attachmentId, url);
        if (downloadUrl) {
          attachmentDownloadUrlCacheRef.current.set(attachmentId, downloadUrl);
        }
        setAttachmentPreview({
          isOpen: true,
          url,
          downloadUrl: downloadUrl || null,
          fileName: fileName || 'Document',
        });
      } else {
        toast.error('Unable to open document: no URL returned');
      }
    } catch (err) {
      console.error('Failed to open attachment', err);
      toast.error('Failed to open document. Please try again.');
    }
  };
  // Helper functions to determine field states based on payroll settings
  const isDateOnlyInRange = (dateValue, from, to) => {
    if (!dateValue || !from || !to) return false;
    const d = new Date(dateValue);
    const f = new Date(from);
    const t = new Date(to);
    if (Number.isNaN(d.getTime()) || Number.isNaN(f.getTime()) || Number.isNaN(t.getTime())) {
      return false;
    }
    const dd = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const ff = new Date(f.getFullYear(), f.getMonth(), f.getDate());
    const tt = new Date(t.getFullYear(), t.getMonth(), t.getDate());
    return dd >= ff && dd <= tt;
  };

  const evaluateInvestmentWindowsAccess = () => {
    const raw = payrollSettings?.investmentWindows;
    if (!raw) return null;
    const windows = typeof raw === "string"
      ? (() => {
          try {
            return JSON.parse(raw);
          } catch {
            return null;
          }
        })()
      : raw;
    if (!windows || typeof windows !== "object") return null;

    const today = new Date();
    const month = today.getMonth() + 1; // 1-12
    const day = today.getDate();
    const months = windows.months || {};

    // New format: per-month fromDay/toDay + audience flags
    const monthCfg =
      months[month] ||
      months[String(month)] ||
      null;

    const hasMonthMap =
      months &&
      typeof months === "object" &&
      Object.keys(months).length > 0;

    if (hasMonthMap) {
      if (!monthCfg || !monthCfg.enabled) return false;
      const fromDay = Math.max(1, Number(monthCfg.fromDay) || 1);
      let toDay = Math.max(fromDay, Number(monthCfg.toDay) || 31);
      if (day < fromDay || day > toDay) return false;

      const allowAll = !!monthCfg.allowAll;
      const allowNewJoinees = !!monthCfg.allowNewJoinees;
      const allowSelection = !!monthCfg.allowSelection;
      const selectedIds = Array.isArray(monthCfg.selectedEmployeeIds)
        ? monthCfg.selectedEmployeeIds.map(Number)
        : [];

      if (allowAll) return true;

      if (allowSelection) {
        const empId = Number(employee?.id);
        if (empId && selectedIds.includes(empId)) return true;
      }

      if (allowNewJoinees) {
        const dojRaw =
          employee?.dateOfJoin ||
          employee?.joiningDate ||
          employee?.joining_date;
        if (dojRaw) {
          const doj = new Date(dojRaw);
          if (
            !Number.isNaN(doj.getTime()) &&
            doj.getMonth() + 1 === month &&
            doj.getDate() >= fromDay &&
            doj.getDate() <= toDay
          ) {
            return true;
          }
        }
      }

      // No audience matched
      if (!allowAll && !allowNewJoinees && !allowSelection) {
        // Defaults: treat as all when no flags set
        return true;
      }
      return false;
    }

    // Legacy top-level scope + fromDate/toDate
    const scope = String(windows.scope || "all").toLowerCase();
    if (scope === "selection") {
      const empId = Number(employee?.id);
      const ids = Array.isArray(windows.selectedEmployeeIds)
        ? windows.selectedEmployeeIds.map(Number)
        : [];
      if (!empId || !ids.includes(empId)) return false;
      return isDateOnlyInRange(
        today,
        windows.selectionFromDate,
        windows.selectionToDate
      );
    }

    const active = Object.values(months).filter(
      (cfg) =>
        cfg?.enabled &&
        cfg.fromDate &&
        cfg.toDate &&
        isDateOnlyInRange(today, cfg.fromDate, cfg.toDate)
    );
    if (!active.length) return false;

    if (scope === "new_joinees") {
      const doj =
        employee?.dateOfJoin ||
        employee?.joiningDate ||
        employee?.joining_date;
      if (!doj) return false;
      return active.some((w) =>
        isDateOnlyInRange(doj, w.fromDate, w.toDate)
      );
    }

    return true; // all
  };

  const isInvestmentDeclarationReadOnly = () => {
    // View mode handled separately; Tax year lock blocks everyone (incl. admin).
    if (payrollSettings) {
      const lockedList = Array.isArray(payrollSettings.lockedTaxYears)
        ? payrollSettings.lockedTaxYears.map(normalizeFinancialYearKey)
        : [];
      const currentFy = normalizeFinancialYearKey(getEffectiveFinancialYear());
      const activeSettingFy = normalizeFinancialYearKey(payrollSettings.taxYear);
      const yearLocked =
        (currentFy && lockedList.includes(currentFy)) ||
        (!!payrollSettings.isTaxYearLocked &&
          (!activeSettingFy || financialYearsMatch(currentFy, activeSettingFy)));
      if (yearLocked) return true;
    }

    // Payroll Actions / Run Payroll admin edit: may edit outside Declaration windows
    // and even when Declaration master toggle is OFF (FY lock above still applies).
    if (adminBypassWindows && mode !== "view") {
      return false;
    }

    // No settings loaded → employees must not edit (fail closed).
    if (!payrollSettings) {
      return true;
    }

    if (!isInvestmentDeclarationEnabled) {
      return true;
    }

    const windowsAllowed = evaluateInvestmentWindowsAccess();
    if (windowsAllowed !== null) {
      return !windowsAllowed;
    }

    const invMode = getInvestmentDeclarationMode?.() || "manual";

    if (invMode === "always") {
      return false;
    }

    if (invMode === "auto") {
      return !isWithinInvestmentWindow();
    }

    return !isWithinInvestmentWindow();
  };

  const isProofUploadReadOnly = () => {
    if (adminBypassWindows && mode !== "view") {
      return false;
    }

    // Fail closed when settings missing
    if (!payrollSettings) {
      return true;
    }

    if (!isInvestmentProofEnabled) {
      return true;
    }

    const modeProof = getInvestmentProofMode?.() || "manual";

    if (modeProof === "always") {
      return false;
    }

    if (modeProof === "auto") {
      return !isWithinProofWindow();
    }

    return !isWithinProofWindow();
  };

  const isProofUploadDisabled = () => {
    // Returns true if proof upload is disabled (read-only or expired/hidden)
    return isProofUploadReadOnly();
  };

  const isTaxRegimeLocked = () => {
    // Admin/HR (or Payroll Actions edit) can always change
    if (isAdminOrHr && mode !== "view") return false;
    // Company-wide hard lock
    if (payrollSettings?.isTaxRegimeLocked) return true;
    // Employee already used their one switch this FY
    if (isTaxRegimeSwitchLocked) return true;
    return false;
  };

  const getTaxRegimeLockReason = () => {
    if (isAdminOrHr && mode !== "view") return "";
    if (payrollSettings?.isTaxRegimeLocked) {
      return "Tax regime change is locked for employees (company setting).";
    }
    if (isTaxRegimeSwitchLocked) {
      return "You already changed and saved your tax regime for this year. Contact Payroll/Admin to change it.";
    }
    return "";
  };

  // Helper functions to check if current date is within windows
  const isWithinInvestmentWindow = () => {
    
    if (!payrollSettings) {
      return false;
    }
    
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth(); // 0-11
    const currentDay = today.getDate();
    
    
    if (payrollSettings.investmentMode === 'AUTO_MONTHLY') {
      // Auto mode - check if within monthly window (applies to every month)
      const fromDay = parseInt(payrollSettings.investmentFromDay) || 1;
      const toDay = parseInt(payrollSettings.investmentToDay) || 31;
      
      // Use currentYear and currentMonth for proper date validation
      console.debug(`🔍 Investment Window: ${currentYear}-${currentMonth + 1}, Day ${currentDay} (range: ${fromDay}-${toDay})`);
      
      const result = currentDay >= fromDay && currentDay <= toDay;
      return result;
    } else {
      // Manual mode - check if before manual end date
      const endDate = payrollSettings.investmentEndDate;
      
      if (endDate) {
        const endDateTime = new Date(endDate);
        // Treat manual investment end date as inclusive for the entire calendar day
        endDateTime.setHours(23, 59, 59, 999);
        const result = today <= endDateTime;
        return result;
      }
      return false;
    }
  };

  const isWithinProofWindow = () => {
    
    if (!payrollSettings) {
      return false;
    }
    
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1; // 1-12
    const currentDay = today.getDate();
    
    
    if (payrollSettings.proofMode === 'AUTO_YEARLY') {
      // Auto mode - check if within yearly window for specific month
      const proofMonth = parseInt(payrollSettings.proofMonth) || 1; // 1-12
      const fromDay = parseInt(payrollSettings.proofFromDay) || 1;
      const toDay = parseInt(payrollSettings.proofToDay) || 31;
      
      // Use currentYear and currentMonth for proper date validation
      console.debug(`🔍 Proof Window: ${currentYear}, Month ${currentMonth} (target: ${proofMonth}), Day ${currentDay} (range: ${fromDay}-${toDay})`);
      
      // Check if current month matches proof month
      if (currentMonth === proofMonth) {
        const result = currentDay >= fromDay && currentDay <= toDay;
        return result;
      }
      return false;
    } else {
      // Manual mode - check if before manual end date
      const endDate = payrollSettings.proofEndDate;
      
      if (endDate) {
        const endDateTime = new Date(endDate);
        // Treat manual proof end date as inclusive for the entire calendar day
        endDateTime.setHours(23, 59, 59, 999);
        const result = today <= endDateTime;
        return result;
      }
      return false;
    }
  };

  // --------------------- NEW: For Proof Documents ---------------------
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [declarationId, setDeclarationId] = useState(declarationHeader?.id || null);

  useEffect(() => {
    if (declarationHeader?.id) {
      setDeclarationId(declarationHeader.id);
    }
  }, [declarationHeader?.id]);

  // Initialize default HRA and House Property entries if they don't exist
  useEffect(() => {
    if (!declarationData.hraEntries || declarationData.hraEntries.length === 0) {
      setDeclarationData(prev => ({
        ...prev,
        hraEntries: [{
          cityType: 'metro',
          totalRentPaid: '',
          landlordPan: '',
          rentalAddress: '',
        }]
      }));
    }
    
    if (!declarationData.housePropertyEntries || declarationData.housePropertyEntries.length === 0) {
      setDeclarationData(prev => ({
        ...prev,
        housePropertyEntries: [{
          propertyType: 'self-occupied',
          loanTakenBeforeApril1999: false,
          interest: '',
          grossAnnualValue: '',
          municipalTaxes: '',
          address: '',
        }]
      }));
    }
  }, []);

  const getFieldAttachment = (sectionName) => {
    const normalize = (name) => {
      if (!name) return '';
      let n = name.toString().toLowerCase().trim();
      // Backwards-compatibility: older data may use longer HRA label
      n = n.replace('house rent allowance (hra) - ', 'hra - ');
      // Normalize various 80D labels to a single key
      n = n.replace('self medical insurance (80d)', 'section 80d - medical insurance');
      n = n.replace('section 80d', 'section 80d - medical insurance');
      // Normalize NPS 80CCD(1B) label variants
      n = n.replace('section 80ccd(1b)', 'nps additional contribution (80ccd(1b))');
      // Normalize 80TTA variations
      n = n.replace('interest on savings (80tta)', 'interest on savings account (80tta)');
      n = n.replace('interest on savings account (80tta)', 'interest on savings account (80tta)');
      // Normalize 80TTB variations
      n = n.replace('interest on deposits (80ttb)', 'interest on deposits (80ttb)');
      return n;
    };

    const target = normalize(sectionName);

    const match = uploadedDocuments.find((doc) => normalize(doc.documentName) === target);

    // If we have a match but it somehow lacks fileName (older data, or mapping
    // was missing), derive a filename from fileUrl as a last-resort fallback
    // so callers like SectionUpload always see a stable name.
    const hydratedMatch =
      match && !match.fileName && match.fileUrl
        ? {
            ...match,
            fileName: (match.fileUrl || '').split('/').pop() || undefined,
          }
        : match;

    return hydratedMatch;
  };

  useEffect(() => {
    if (Array.isArray(attachments)) {
      const mapped = attachments.map((att) => {
        const pathName = (att.filePath || '').split('/').pop() || undefined;
        const effectiveFileName =
          att.originalFileName || att.downloadFileName || att.fileName || pathName;

        return {
          documentName: att.sectionName || att.fieldName || 'Document',
          // Use effectiveFileName for UI display, deriving it from filePath when
          // original/download/fileName are missing (older data).
          fileName: effectiveFileName,
          // Expose explicit original/download names for consumers that care
          originalFileName: effectiveFileName,
          downloadFileName: att.downloadFileName || null,
          size: att.fileSize,
          status: (att.status || 'PENDING').toLowerCase(),
          lastUpdated: att.uploadedAt || att.reviewedAt || new Date().toISOString(),
          comment: att.comments || '',
          isSectionLevel: !att.fieldName,
          attachmentId: att.id,
          fileUrl: att.filePath,
        };
      });
      setUploadedDocuments(mapped);
    }
  }, [attachments]);

  // Fetch selected employee's investment declaration for admin view
  useEffect(() => {
    const loadInvestment = async () => {
      const empId = resolveEmployeeId(employee);
      const fy =
        financialYearFromPayMonth(payMonth) ||
        financialYear ||
        getCurrentFinancialYear();
      if (!empId || !fy) return;
      try {
        const resp = await payrollService.getInvestmentData(empId, fy);
        if (resp?.success && resp.data) {
          const { header, hra, section80c, via, houseProperty, attachments: atts } = resp.data;
          if (header?.id) setDeclarationId(header.id);
          setPrevEmployment({
            employerName: header?.prevEmployerName?.toString() || '',
            employerPan: header?.prevEmployerPan?.toString() || '',
            employerTan: header?.prevEmployerTan?.toString() || '',
            grossSalary:
              header?.prevGrossSalary != null ? header.prevGrossSalary.toString() : '',
            taxDeducted:
              header?.prevTaxDeducted != null ? header.prevTaxDeducted.toString() : '',
            professionalTax:
              header?.prevProfessionalTax != null
                ? header.prevProfessionalTax.toString()
                : '',
          });
          // Sync tax regime selection with backend header if present; track initial for cancel popup (only when regime changed)
          if (header?.taxRegime) {
            const regime = header.taxRegime.toString().toLowerCase() === 'old' ? 'old' : 'new';
            setSelectedTaxRegime(regime);
            initialSelectedTaxRegimeRef.current = regime;
          } else {
            const fallback = resolveTaxRegime(
              employee?.taxRegime || payrollSettings?.defaultTaxRegime || "NEW"
            );
            setSelectedTaxRegime(fallback);
            initialSelectedTaxRegimeRef.current = fallback;
          }
          setIsTaxRegimeSwitchLocked(!!header?.isTaxRegimeSwitchLocked);
          setIsPrevEmploymentSubmitted(!!header?.isPrevEmploymentSubmitted);
          // Rules handling (defensive)
          const rules = resp.data.rules || {};
          const inv = rules.investment || {};
          const prf = rules.proof || {};
          const tax = rules.taxRegime || {};
          // Note: Investment declaration, proof upload, and tax regime rules are now handled by payroll settings props
          // The old state-based approach has been replaced with prop-based helper functions

          const mappedHra =
            Array.isArray(hra) && hra.length
              ? hra.map((row) => ({
                  cityType: row.isMetro ? 'metro' : 'non-metro',
                  totalRentPaid: row.totalRentPaid?.toString() ?? '',
                  landlordPan: row.landlordPan ?? '',
                  rentalAddress: row.rentalAddress ?? '',
                }))
              : [
                  {
                    cityType: 'metro',
                    totalRentPaid: '',
                    landlordPan: '',
                    rentalAddress: '',
                  },
                ];

          const mappedHouseProperty =
            Array.isArray(houseProperty) && houseProperty.length
              ? houseProperty.map((row) => ({
                  propertyType: occupancyToUiPropertyType(row.occupancy),
                  loanTakenBeforeApril1999: !!row.loanBeforeApr1999,
                  interest: row.interestPaidOnBorrowedCapital?.toString() ?? '',
                  grossAnnualValue:
                    row.grossAnnualValue?.toString() ??
                    row.gross_annual_value?.toString() ??
                    '',
                  municipalTaxes:
                    row.municipalTaxes?.toString() ??
                    row.municipal_taxes?.toString() ??
                    '',
                  address: row.propertyAddress ?? '',
                }))
              : [
                  {
                    propertyType: 'self-occupied',
                    loanTakenBeforeApril1999: false,
                    interest: '',
                    grossAnnualValue: '',
                    municipalTaxes: '',
                    address: '',
                  },
                ];

          // Initialize override flags from the live header so the
          // Employee Access Overrides checkboxes reflect DB values.
          if (header) {
            const overrideState = {
              allowAllEdits: !!header.allowAllEdits,
              allowPrevEmploymentEdit: !!header.allowPrevEmploymentEdit,
              allowHraEdit: !!header.allowHraEdit,
              allowSection80CEdit: !!header.allowSection80CEdit,
              allowViaEdit: !!header.allowViaEdit,
              allowHousePropertyEdit: !!header.allowHousePropertyEdit,
            };
            setAllowAllEdits(overrideState.allowAllEdits);
            setAllowPrevEmploymentEdit(overrideState.allowPrevEmploymentEdit);
            setAllowHraEdit(overrideState.allowHraEdit);
            setAllowSection80CEdit(overrideState.allowSection80CEdit);
            setAllowViaEdit(overrideState.allowViaEdit);
            setAllowHousePropertyEdit(overrideState.allowHousePropertyEdit);
            // Also update the original state (what gets used when saving form sections)
            setOriginalOverrideState(overrideState);
          }

          setDeclarationData((prev) => ({
            ...prev,
            hraEntries: mappedHra,
            section80C: {
              ...prev.section80C,
              lifeInsurance:
                section80c?.lifeInsurancePremium?.toString() || prev.section80C.lifeInsurance,
              ppf: section80c?.ppf?.toString() || prev.section80C.ppf,
              vpf: section80c?.vpf?.toString() || prev.section80C.vpf,
              pensionFund:
                section80c?.pensionFund?.toString() || prev.section80C.pensionFund,
              nsc: section80c?.nsc?.toString() || prev.section80C.nsc,
              nscInterest:
                section80c?.nscInterestReinvested?.toString() || prev.section80C.nscInterest,
              ulip: section80c?.ulip?.toString() || prev.section80C.ulip,
              elss: section80c?.elss?.toString() || prev.section80C.elss,
              tuitionFees:
                section80c?.tuitionFees?.toString() || prev.section80C.tuitionFees,
              houseLoanPrincipal:
                section80c?.principalHousingLoan?.toString() ||
                prev.section80C.houseLoanPrincipal,
              registrationCharges:
                section80c?.registrationCharges?.toString() ||
                prev.section80C.registrationCharges,
              sukanyaSamriddhi:
                section80c?.sukanyaSamriddhi?.toString() ||
                prev.section80C.sukanyaSamriddhi,
              infrastructureBonds:
                section80c?.infrastructureBonds?.toString() ||
                prev.section80C.infrastructureBonds,
              fixedDeposit:
                section80c?.bankFixedDeposit?.toString() || prev.section80C.fixedDeposit,
            },
            sectionVIA: {
              ...prev.sectionVIA,
              section80D: {
                ...prev.sectionVIA.section80D,
                self:
                  via?.selfMedicalInsurance?.toString() ||
                  prev.sectionVIA.section80D.self,
                parents: (() => {
                  const base =
                    via?.parentsMedicalInsurance?.toString() ||
                    prev.sectionVIA.section80D.parents;
                  const legacySenior = via?.seniorCitizenParentsMedical;
                  if (
                    legacySenior != null &&
                    Number(legacySenior) > 0 &&
                    !via?.parentsMedicalInsurance
                  ) {
                    return legacySenior.toString();
                  }
                  return base;
                })(),
                isSelfSeniorCitizen:
                  via?.isSelfSeniorCitizen ??
                  prev.sectionVIA.section80D.isSelfSeniorCitizen ??
                  false,
                isParentsSeniorCitizen: !!(
                  via?.isParentsSeniorCitizen ??
                  (Number(via?.seniorCitizenParentsMedical || 0) > 0 ||
                    prev.sectionVIA.section80D.isParentsSeniorCitizen)
                ),
                selfPreventiveHealthCheckup:
                  via?.selfPreventiveHealthCheckup?.toString() ??
                  via?.preventiveHealthCheckup?.toString() ??
                  prev.sectionVIA.section80D.selfPreventiveHealthCheckup ??
                  '',
                parentsPreventiveHealthCheckup:
                  via?.parentsPreventiveHealthCheckup?.toString() ??
                  prev.sectionVIA.section80D.parentsPreventiveHealthCheckup ??
                  '',
              },
              otherInvestments: {
                ...prev.sectionVIA.otherInvestments,
                section80E:
                  via?.interestEducationLoan?.toString() ||
                  prev.sectionVIA.otherInvestments.section80E,
                section80CCD1B:
                  via?.npsAdditionalContribution?.toString() ||
                  prev.sectionVIA.otherInvestments.section80CCD1B,
                section80EE:
                  via?.interestOnHomeLoan80EE?.toString() ||
                  prev.sectionVIA.otherInvestments.section80EE,
                section80EEA:
                  via?.interestOnHomeLoan80EEA?.toString() ||
                  prev.sectionVIA.otherInvestments.section80EEA,
                section80EEB:
                  via?.interestOnHomeLoan80EEB?.toString() ||
                  prev.sectionVIA.otherInvestments.section80EEB,
                section80TTA:
                  via?.interestOnSavings80TTA?.toString() ||
                  prev.sectionVIA.otherInvestments.section80TTA,
              },
            },
            housePropertyEntries: mappedHouseProperty,
          }));

          if (!Array.isArray(attachments) && Array.isArray(atts)) {
            const mapped = atts.map((att) => ({
              documentName: att.sectionName || att.fieldName || 'Document',
              fileName: att.fileName,
              size: att.fileSize,
              status: (att.status || 'PENDING').toLowerCase(),
              lastUpdated: att.uploadedAt || att.reviewedAt || new Date().toISOString(),
              comment: att.comments || '',
              isSectionLevel: !att.fieldName,
              attachmentId: att.id,
              fileUrl: att.filePath,
            }));
            setUploadedDocuments(mapped);
          }
        } else {
          // 200 with success: false and "Investment declaration not found for given employee/year" – no toast
          const msg = (resp?.message || '').toLowerCase();
          if (!msg.includes('investment declaration not found')) {
            toast.error('Failed to load investment declaration data. Please try again.');
          } else {
            const fallback = resolveTaxRegime(
              payrollSettings?.defaultTaxRegime || employee?.taxRegime || "NEW"
            );
            setSelectedTaxRegime(fallback);
            initialSelectedTaxRegimeRef.current = fallback;
            setIsTaxRegimeSwitchLocked(false);
          }
        }
      } catch (e) {
        // 404 or response body "Investment declaration not found for given employee/year" – don't show errors
        if (e?.response?.status === 404) {
          const fallback = resolveTaxRegime(
            payrollSettings?.defaultTaxRegime || employee?.taxRegime || "NEW"
          );
          setSelectedTaxRegime(fallback);
          initialSelectedTaxRegimeRef.current = fallback;
          setIsTaxRegimeSwitchLocked(false);
          return;
        }
        const errorMessage = e?.response?.data?.message || e?.message || '';
        if (errorMessage.toLowerCase().includes('investment declaration not found')) {
          const fallback = resolveTaxRegime(
            payrollSettings?.defaultTaxRegime || employee?.taxRegime || "NEW"
          );
          setSelectedTaxRegime(fallback);
          initialSelectedTaxRegimeRef.current = fallback;
          setIsTaxRegimeSwitchLocked(false);
          return;
        }
        console.error('Failed to load investment data for admin:', e);
        toast.error('Failed to load investment declaration data. Please try again.');
      }
    };
    loadInvestment();
  }, [employee?.id, employee?.employeeId, financialYear, payMonth, payrollSettings?.defaultTaxRegime]);

  // Load full employee profile so experiences/isFresher can drive Previous Employment tab visibility
  useEffect(() => {
    const loadEmployeeProfile = async () => {
      if (!employee?.id) {
        setEmployeeProfile(null);
        return;
      }
      try {
        const resp = await employeeService.getEmployee(employee.id);
        setEmployeeProfile(resp?.data || resp || null);
      } catch (e) {
        console.error('Failed to load employee profile for investment modal:', e);
        toast.error('Failed to load employee profile. Please try again.');
        setEmployeeProfile(null);
      }
    };
    loadEmployeeProfile();
  }, [employee?.id]);

  // Reset initial tax regime ref when modal closes so next open doesn't use stale value
  useEffect(() => {
    if (!employee?.id) initialSelectedTaxRegimeRef.current = null;
  }, [employee?.id]);

  useEffect(() => {
    if (!showAccessInfo) return undefined;
    const onDocClick = (e) => {
      if (accessInfoRef.current && !accessInfoRef.current.contains(e.target)) {
        setShowAccessInfo(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [showAccessInfo]);

  useEffect(() => {
    if (!showHpAggInfo) return undefined;
    const onDocClick = (e) => {
      if (hpAggInfoRef.current && !hpAggInfoRef.current.contains(e.target)) {
        setShowHpAggInfo(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [showHpAggInfo]);

  useEffect(() => {
    if (!show80DInfo) return undefined;
    const onDocClick = (e) => {
      if (section80DInfoRef.current && !section80DInfoRef.current.contains(e.target)) {
        setShow80DInfo(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [show80DInfo]);

  useEffect(() => {
    if (!showHraInfo) return undefined;
    const onDocClick = (e) => {
      if (hraInfoRef.current && !hraInfoRef.current.contains(e.target)) {
        setShowHraInfo(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [showHraInfo]);

  useEffect(() => {
    if (!show80CInfo) return undefined;
    const onDocClick = (e) => {
      if (section80CInfoRef.current && !section80CInfoRef.current.contains(e.target)) {
        setShow80CInfo(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [show80CInfo]);

  useEffect(() => {
    const empId = resolveEmployeeId(employee);
    if (!empId) return undefined;
    const month = resolveTaxPlanningPayMonth({ financialYear, payMonth });
    let cancelled = false;
    (async () => {
      setSalaryTaxBases((prev) => ({ ...prev, loading: true }));
      try {
        const res = await payrollOpsService.getTaxLiability(empId, month);
        const ill = res?.data?.illustration || res?.illustration || {};
        if (!cancelled) {
          setSalaryTaxBases({
            basicAnnual: Number(ill.basicAnnual) || 0,
            hraAnnual: Number(ill.hraAnnual) || 0,
            loading: false,
            loaded: true,
          });
        }
      } catch {
        if (!cancelled) {
          setSalaryTaxBases({
            basicAnnual: 0,
            hraAnnual: 0,
            loading: false,
            loaded: false,
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [employee, payMonth, financialYear, illustrationRefreshKey]);

  // Enforce tax regime lock based on payroll settings / one-switch lock
  useEffect(() => {
    if (isAdminOrHr || mode === "view") return;
    if (!isTaxRegimeLocked()) return;
    // Prefer currently selected / default over stale employee.taxRegime when locked
    const lockedRegime = resolveTaxRegime(
      selectedTaxRegime || companyDefaultRegime || employee?.taxRegime || "NEW"
    );
    setSelectedTaxRegime(lockedRegime);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-assert when lock flags change
  }, [
    payrollSettings?.isTaxRegimeLocked,
    isTaxRegimeSwitchLocked,
    isAdminOrHr,
    mode,
  ]);

  const saveTaxRegime = async (newRegime) => {
    if (mode === "view") return;
    if (!isAdminOrHr && isTaxRegimeLocked()) {
      toast.error(
        getTaxRegimeLockReason() || "Tax regime change is not allowed."
      );
      return;
    }
    const previous = selectedTaxRegime;
    setSelectedTaxRegime(newRegime);
    if (employee) {
      employee.taxRegime = newRegime;
    }
    if (!employee?.id && !resolveEmployeeId(employee)) return;
    try {
      const empId = resolveEmployeeId(employee);
      const payload = {
        financialYear: getEffectiveFinancialYear(),
        taxRegime: newRegime === "old" ? "OLD" : "NEW",
      };
      const result = await payrollService.saveInvestmentData(empId, payload);
      if (!result?.success) {
        setSelectedTaxRegime(previous);
        if (employee) employee.taxRegime = previous;
        toast.error(result?.message || "Failed to save tax regime change");
        return;
      }
      // Employee used their one switch for the year
      if (!isAdminOrHr && previous !== newRegime) {
        setIsTaxRegimeSwitchLocked(true);
      }
      const header = result?.data?.header;
      if (header?.isTaxRegimeSwitchLocked != null) {
        setIsTaxRegimeSwitchLocked(!!header.isTaxRegimeSwitchLocked);
      }
      if (header?.id) setDeclarationId(header.id);
      if (newRegime === "old") {
        setCurrentSectionIndex(0);
      }
      setIllustrationRefreshKey((k) => k + 1);
      if (typeof onSave === "function") {
        onSave({
          type: "tax_regime_changed",
          taxRegime: newRegime,
          declarationData,
        });
      }
    } catch (error) {
      setSelectedTaxRegime(previous);
      if (employee) employee.taxRegime = previous;
      console.error("Error saving tax regime change:", error);
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to save tax regime change"
      );
    }
  };

  const ensureDeclarationId = async () => {
    if (declarationId) return declarationId;

    const empId = resolveEmployeeId(employee);
    if (!empId) {
      throw new Error(
        'Employee ID is not available for initializing declaration. Open Tax Planning from your linked employee account, or ask Payroll to open it from Actions.'
      );
    }

    const fy = getEffectiveFinancialYear();

    const json = await payrollService.saveInvestmentData(empId, {
      financialYear: fy,
      taxRegime: selectedTaxRegime === 'old' ? 'OLD' : 'NEW',
    });
    const newId = json?.data?.header?.id;
    if (!newId) {
      throw new Error('Declaration header ID missing in response');
    }

    setDeclarationId(newId);
    return newId;
  };

  const handleAdminDocumentUpload = async ({ file, sectionName, isSectionLevel }, sectionId) => {
    const effectiveDeclarationId = await ensureDeclarationId();

    // Use employee.id/empCode and sectionName to build deterministic R2 keys and download names
    const employeeId = employee?.id;
    const empCode = employee?.empCode || employee?.employeeCode || employee?.code || '';
    const slotKey = sectionId != null ? `${sectionName}-${sectionId}` : sectionName;

    // Derive extension from the uploaded file name
    const dotIndex = file.name.lastIndexOf('.');
    const ext = dotIndex !== -1 ? file.name.substring(dotIndex) : '';

    // R2 storage file name should be label-based, e.g. "Life Insurance Premium.pdf"
    const storageFileName = `${sectionName}${ext}`;

    const storageInfo = await uploadFileToR2(file, {
      employeeId,
      slotKey,
      storageFileName,
    });

    // Download file name in DB should be like PS0001_Life_Insurance_Premium.pdf
    const safeLabel = sectionName.replace(/[^A-Za-z0-9]+/g, '_');
    const downloadFileName = empCode
      ? `${empCode}_${safeLabel}${ext}`
      : `${safeLabel}${ext}`;

    const attachment = await createInvestmentAttachment(effectiveDeclarationId, {
      sectionName,
      fieldName: isSectionLevel ? null : sectionName,
      r2Path: storageInfo.r2Path,
      fileUrl: storageInfo.fileUrl,
      fileName: storageInfo.fileName || file.name,
      fileSize: storageInfo.fileSize || file.size,
      originalFileName: file.name,
      downloadFileName,
    });

    // Invalidate any cached signed URL for this attachment so preview/download always loads the latest file.
    if (attachment?.id) {
      attachmentUrlCacheRef.current.delete(attachment.id);
    }

    const docRecord = {
      documentName: sectionName,
      fileName: attachment.fileName,
      size: attachment.fileSize,
      status: (attachment.status || 'PENDING').toLowerCase(),
      lastUpdated: attachment.uploadedAt || new Date().toISOString(),
      comment: attachment.comments || '',
      isSectionLevel,
      sectionId,
      attachmentId: attachment.id,
      fileUrl: attachment.filePath,
    };

    setUploadedDocuments((prev) => [...prev, docRecord]);

    return { attachmentId: attachment.id };
  };

  const handleApproveAttachment = async ({ attachmentId }) => {
    const updated = await reviewInvestmentAttachment(attachmentId, {
      status: 'APPROVED',
      comment: null,
    });

    setUploadedDocuments((prev) =>
      prev.map((doc) =>
        doc.attachmentId === attachmentId
          ? {
              ...doc,
              status: (updated.status || 'APPROVED').toLowerCase(),
              lastUpdated: updated.reviewedAt || doc.lastUpdated,
              comment: updated.comments || doc.comment,
            }
          : doc
      )
    );
    
    // Notify parent component to refresh data (for list/grid view updates)
    if (typeof onSave === 'function') {
      onSave({ 
        type: 'attachment_approved',
        attachmentId,
        declarationData 
      });
    }
  };

  const handleRejectAttachment = async ({ attachmentId, reason }) => {
    const updated = await reviewInvestmentAttachment(attachmentId, {
      status: 'REJECTED',
      comment: reason,
    });

    setUploadedDocuments((prev) =>
      prev.map((doc) =>
        doc.attachmentId === attachmentId
          ? {
              ...doc,
              status: (updated.status || 'REJECTED').toLowerCase(),
              lastUpdated: updated.reviewedAt || doc.lastUpdated,
              comment: updated.comments || doc.comment,
            }
          : doc
      )
    );
    
    // Notify parent component to refresh data (for list/grid view updates)
    if (typeof onSave === 'function') {
      onSave({ 
        type: 'attachment_rejected',
        attachmentId,
        declarationData 
      });
    }
  };

  const handleRemoveAttachment = async (attachmentId) => {
    await deleteInvestmentAttachment(attachmentId);
    setUploadedDocuments((prev) => prev.filter((doc) => doc.attachmentId !== attachmentId));
  };

  const handleBulkAction = (sectionName, action, reason = '') => {
    // TODO: optionally wire bulk approve/reject via reviewInvestmentAttachment for all matching docs
  };

  // Backwards-compatibility wrappers so existing JSX still compiles while routing
  // everything through the new admin upload handler.
  const handleProofUpload = (payload) => handleAdminDocumentUpload(payload);
  const handleDocumentUpload = (payload, sectionId) =>
    handleAdminDocumentUpload(payload, sectionId);

  

  // --------------------- SECTION NAVIGATION HELPERS ---------------------
  const getCurrentSection = () => {
    if (!tabs?.length) return null;
    const idx = Math.min(
      Math.max(0, currentSectionIndex),
      tabs.length - 1
    );
    return tabs[idx];
  };

  const goToNextSection = () => {
    if (currentSectionIndex < tabs.length - 1) {
      const nextIdx = currentSectionIndex + 1;
      setCurrentSectionIndex(nextIdx);
      if (tabs[nextIdx]?.id === 'taxLiability') {
        setIllustrationRefreshKey((k) => k + 1);
      }
    }
  };

  const goToPreviousSection = () => {
    if (currentSectionIndex > 0) {
      setCurrentSectionIndex(currentSectionIndex - 1);
    }
  };

  const goToSection = (sectionId) => {
    const index = tabs.findIndex(tab => tab.id === sectionId);
    if (index !== -1) {
      setCurrentSectionIndex(index);
      if (sectionId === 'taxLiability') {
        setIllustrationRefreshKey((k) => k + 1);
      }
    }
  };

  // --------------------- SECTION SAVE HELPERS ---------------------
  const saveCurrentSection = async (sectionIdOverride = null) => {
    if (mode === "view" || isInvestmentDeclarationReadOnly()) {
      toast.error(
        "Tax Planning is read-only right now (Declaration off, outside day window, or tax year locked)."
      );
      return;
    }
    const currentSection =
      sectionIdOverride != null
        ? tabs.find((t) => t.id === sectionIdOverride)
        : getCurrentSection();
    if (!currentSection?.id) {
      toast.error('No section available to save');
      return;
    }
    const sectionId = currentSection.id;

    if (sectionId === 'taxLiability') {
      setIllustrationRefreshKey((k) => k + 1);
      return;
    }

    if (!validateCurrentSection(sectionId)) {
      toast.error('Please fix the highlighted fields');
      return;
    }
    
    try {
      // Get the declaration ID first
      const effectiveDeclarationId = await ensureDeclarationId();
      
      // Prepare the data for the current section
      let sectionData = {};

      // Header fields — previous employment only when saving that section
      const headerPart = {
        // Use original override state (not current checkbox state) when saving form sections
        allowAllEdits: originalOverrideState.allowAllEdits,
        allowPrevEmploymentEdit: originalOverrideState.allowPrevEmploymentEdit,
        allowHraEdit: originalOverrideState.allowHraEdit,
        allowSection80CEdit: originalOverrideState.allowSection80CEdit,
        allowViaEdit: originalOverrideState.allowViaEdit,
        allowHousePropertyEdit: originalOverrideState.allowHousePropertyEdit,
      };
      if (sectionId === 'prevEmployment') {
        Object.assign(headerPart, buildPrevEmploymentHeaderPart(), {
          isPrevEmploymentSubmitted: true,
        });
      }
      
      // Map section data based on section ID
      switch (sectionId) {
        case 'prevEmployment':
          // Previous employment is stored in declaration header fields
          sectionData = {};
          break;
        case 'hra':
          sectionData = {
            hra: declarationData.hraEntries.map(entry => ({
              isMetro: entry.cityType === 'metro',
              totalRentPaid: parseFloat(entry.totalRentPaid) || 0,
              landlordPan: entry.landlordPan,
              rentalAddress: entry.rentalAddress
            }))
          };
          break;
        case 'section80C':
          sectionData = {
            section80c: {
              lifeInsurancePremium: parseFloat(declarationData.section80C.lifeInsurance) || 0,
              ppf: parseFloat(declarationData.section80C.ppf) || 0,
              vpf: parseFloat(declarationData.section80C.vpf) || 0,
              pensionFund: parseFloat(declarationData.section80C.pensionFund) || 0,
              nsc: parseFloat(declarationData.section80C.nsc) || 0,
              nscInterestReinvested: parseFloat(declarationData.section80C.nscInterest) || 0,
              ulip: parseFloat(declarationData.section80C.ulip) || 0,
              elss: parseFloat(declarationData.section80C.elss) || 0,
              tuitionFees: parseFloat(declarationData.section80C.tuitionFees) || 0,
              principalHousingLoan: parseFloat(declarationData.section80C.houseLoanPrincipal) || 0,
              registrationCharges: parseFloat(declarationData.section80C.registrationCharges) || 0,
              sukanyaSamriddhi: parseFloat(declarationData.section80C.sukanyaSamriddhi) || 0,
              infrastructureBonds: parseFloat(declarationData.section80C.infrastructureBonds) || 0,
              bankFixedDeposit: parseFloat(declarationData.section80C.fixedDeposit) || 0,
            }
          };
          break;
        case 'viaDeductions': {
          const via = declarationData.sectionVIA || {};
          const d = via.section80D || {};
          const o = via.otherInvestments || {};
          sectionData = {
            via: {
              selfMedicalInsurance: parseFloat(d.self) || 0,
              parentsMedicalInsurance: parseFloat(d.parents) || 0,
              seniorCitizenParentsMedical: 0,
              isSelfSeniorCitizen: !!d.isSelfSeniorCitizen,
              isParentsSeniorCitizen: !!d.isParentsSeniorCitizen,
              selfPreventiveHealthCheckup:
                parseFloat(d.selfPreventiveHealthCheckup) || 0,
              parentsPreventiveHealthCheckup:
                parseFloat(d.parentsPreventiveHealthCheckup) || 0,
              preventiveHealthCheckup: 0,
              interestEducationLoan: parseFloat(o.section80E) || 0,
              npsAdditionalContribution: parseFloat(o.section80CCD1B) || 0,
              interestOnHomeLoan80EE: parseFloat(o.section80EE) || 0,
              interestOnHomeLoan80EEA: parseFloat(o.section80EEA) || 0,
              interestOnHomeLoan80EEB: parseFloat(o.section80EEB) || 0,
              donations80G: 0,
              interestOnSavings80TTA: parseFloat(o.section80TTA) || 0,
              interestOnDeposits80TTB: 0,
            }
          };
          break;
        }
        case 'houseProperty':
          sectionData = {
            houseProperty: (declarationData.housePropertyEntries || []).map((entry) => {
              const occupancy = uiPropertyTypeToOccupancy(entry.propertyType);
              const rentable = isRentablePropertyType(entry.propertyType);
              return {
                occupancy,
                loanBeforeApr1999: !!entry.loanTakenBeforeApril1999,
                interestPaidOnBorrowedCapital: parseFloat(entry.interest) || 0,
                grossAnnualValue: rentable ? parseFloat(entry.grossAnnualValue) || 0 : 0,
                municipalTaxes: rentable ? parseFloat(entry.municipalTaxes) || 0 : 0,
                propertyAddress: entry.address || '',
              };
            }),
          };
          break;
        default:
          console.warn('Unknown section ID:', sectionId);
          return;
      }
      
      // Make the API call to save the section data using shared API helper
      const payload = {
        financialYear: getEffectiveFinancialYear(),
        taxRegime: selectedTaxRegime === 'old' ? 'OLD' : 'NEW',
        ...headerPart,
        ...sectionData,
      };

      const empId = resolveEmployeeId(employee);
      if (!empId) {
        toast.error(
          'Employee ID is not available. Open Tax Planning from your linked employee account, or ask Payroll to open it from Actions.'
        );
        return;
      }

      const result = await payrollService.saveInvestmentData(empId, payload);

      if (!result?.success) {
        toast.error(result?.message || `Failed to save ${currentSection.label} details`);
        throw new Error('Failed to save section data');
      }
      
      // Mark section as saved
      setSavedSections(prev => new Set([...prev, sectionId]));
      if (sectionId === 'prevEmployment') {
        setIsPrevEmploymentSubmitted(true);
      }
      setErrors({});
      toast.success(`${currentSection.label} details saved successfully!`);

      // After save, treat current regime as "initial" so Cancel won't show popup
      initialSelectedTaxRegimeRef.current = selectedTaxRegime;

      // Notify parent component to refresh data (for list/grid view updates)
      if (typeof onSave === 'function') {
        onSave({ 
          type: 'section_saved',
          sectionId,
          taxRegime: selectedTaxRegime,
          declarationData 
        });
      }
      
      // Auto-navigate to next section
      setTimeout(() => {
        goToNextSection();
      }, 1000);
      setIllustrationRefreshKey((k) => k + 1);
      
    } catch (error) {
      console.error('Error saving section:', error);
      toast.error(`Failed to save ${currentSection.label} details: ${error.message}`);
    }
  };

  const resetCurrentSection = () => {
    const currentSection = getCurrentSection();
    const sectionId = currentSection.id;
    
    // Reset section data based on section type
    switch (sectionId) {
      case 'hra':
        setDeclarationData(prev => ({
          ...prev,
          hraEntries: [
            {
              cityType: 'metro',
              totalRentPaid: '',
              landlordPan: '',
              rentalAddress: '',
            },
          ],
        }));
        break;
      case 'section80C':
        setDeclarationData(prev => ({
          ...prev,
          section80C: {
            lifeInsurance: '',
            ppf: '',
            vpf: '',
            pensionFund: '',
            nsc: '',
            nscInterest: '',
            ulip: '',
            elss: '',
            tuitionFees: '',
            houseLoanPrincipal: '',
            registrationCharges: '',
            sukanyaSamriddhi: '',
            infrastructureBonds: '',
            fixedDeposit: '',
          }
        }));
        break;
      case 'viaDeductions':
        setDeclarationData(prev => ({
          ...prev,
          sectionVIA: {
            section80D: {
              self: '',
              parents: '',
              isSelfSeniorCitizen: false,
              isParentsSeniorCitizen: false,
              selfPreventiveHealthCheckup: '',
              parentsPreventiveHealthCheckup: '',
            },
            otherInvestments: {
              section80E: '',
              section80CCD1B: '',
              section80EE: '',
              section80EEA: '',
              section80EEB: '',
              section80TTA: '',
            },
          }
        }));
        break;
      case 'houseProperty':
        setDeclarationData(prev => ({
          ...prev,
          housePropertyEntries: [
            {
              propertyType: 'self-occupied',
              loanTakenBeforeApril1999: false,
              interest: '',
              grossAnnualValue: '',
              municipalTaxes: '',
              address: '',
            },
          ],
        }));
        break;
    }
  };

  const submitDeclaration = () => {
    // Open confirmation modal based on selected tax regime
    if (selectedTaxRegime === 'old') {
      setSubmitConfirm({ open: true, mode: 'old' });
    } else if (selectedTaxRegime === 'new') {
      setSubmitConfirm({ open: true, mode: 'new' });
    }
  };

  const handleConfirmSubmit = async () => {
    if (mode === "view" || isInvestmentDeclarationReadOnly()) {
      toast.error(
        "Tax Planning is read-only right now (Declaration off, outside day window, or tax year locked)."
      );
      setSubmitConfirm({ open: false, mode: null });
      return;
    }
    try {
      const mode = submitConfirm.mode;
      if (!mode) {
        setSubmitConfirm({ open: false, mode: null });
        return;
      }

      // Build header payload including previous employment values
      const headerPart = {
        ...buildPrevEmploymentHeaderPart(),
        allowAllEdits,
        allowPrevEmploymentEdit,
        allowHraEdit,
        allowSection80CEdit,
        allowViaEdit,
        allowHousePropertyEdit,
      };

      const financialYear = getEffectiveFinancialYear();

      if (mode === 'old') {
        const sections = buildInvestmentSectionsPayload(declarationData);
        const payload = {
          financialYear,
          taxRegime: 'OLD',
          status: 'SUBMITTED',
          ...headerPart,
          ...sections,
        };

        const result = await payrollService.saveInvestmentData(resolveEmployeeId(employee), payload);
        if (!result?.success) {
          console.error('Failed to submit declaration (old regime):', result);
          toast.error(result?.message || 'Failed to submit declaration');
          return;
        }

        // Mark all sections as saved for UI
        const allSections = ['hra', 'section80C', 'viaDeductions', 'houseProperty', 'taxLiability'];
        setSavedSections(new Set(allSections));

        initialSelectedTaxRegimeRef.current = 'old';
        toast.success('Declaration submitted successfully under Old Tax Regime');
        setIllustrationRefreshKey((k) => k + 1);
        goToSection('taxLiability');
        if (typeof onSave === 'function') {
          onSave({
            type: 'submitted',
            taxRegime: 'old',
            financialYear,
            declarationData,
            prevEmployment,
            formData: buildTaxPlanningDeskFormData({
              taxRegime: 'old',
              financialYear,
              declarationData,
              prevEmployment,
              status: 'SUBMITTED',
            }),
          });
        }
        setShowSubmitSuccessModal(true);
      } else if (mode === 'new') {
        // New regime submission flow
        try {
          await payrollService.deleteInvestmentData(
            resolveEmployeeId(employee) || employee.id,
            financialYear
          );
        } catch (err) {
          console.error(
            'Failed to clear old regime investment data before submitting new regime (admin):',
            err
          );

          // If there is simply no existing declaration to delete (404), continue.
          const status = err?.response?.status;
          if (status && status !== 404) {
            toast.error('Failed to clear existing investment data. Please try again.');
            return;
          }
        }

        const payload = {
          financialYear,
          taxRegime: 'NEW',
          status: 'SUBMITTED',
          ...headerPart,
        };

        const result = await payrollService.saveInvestmentData(resolveEmployeeId(employee), payload);
        if (!result?.success) {
          console.error('Failed to submit declaration (new regime):', result);
          toast.error(result?.message || 'Failed to submit declaration');
          return;
        }

        initialSelectedTaxRegimeRef.current = 'new';
        toast.success('Declaration submitted successfully under New Tax Regime');
        setIllustrationRefreshKey((k) => k + 1);
        if (typeof onSave === 'function') {
          onSave({
            type: 'submitted',
            taxRegime: 'new',
            financialYear,
            declarationData,
            prevEmployment,
            formData: buildTaxPlanningDeskFormData({
              taxRegime: 'new',
              financialYear,
              declarationData,
              prevEmployment,
              status: 'SUBMITTED',
            }),
          });
        }
        setShowSubmitSuccessModal(true);
      }
    } catch (error) {
      console.error('Error submitting declaration (admin):', error);
      toast.error(error?.message || 'Error submitting declaration');
    } finally {
      setSubmitConfirm({ open: false, mode: null });
    }
  };

  const handleSubmitSuccessClose = () => {
    setShowSubmitSuccessModal(false);
    onClose();
  };

  // Example "renderProofDocuments" similar to your first snippet
  const renderProofDocuments = () => (
    <div className="space-y-4 mt-6">
      <div className="flex justify-between items-center">
        <h3 className="font-semibold text-lg">Proof Documents</h3>
        {!isReadOnly && (
          <button
            onClick={() => setIsUploadModalOpen(true)}
            className="text-green-500 flex items-center hover:text-green-600 text-sm"
          >
            <FileUp className="mr-1" size={16} />
            Drop Files Here
          </button>
        )}
      </div>

      <div
        className="border overflow-hidden"
        style={{ maxHeight: '300px', overflowY: 'auto' }}
      >
        <table className="w-full">
          <thead className="bg-gray-50 sticky top-0">
            <tr>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Sr. No.</th>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Document Name</th>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">File Name</th>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Last Updated</th>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Comments</th>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {uploadedDocuments.map((doc, index) => (
              <tr key={index} className="hover:bg-gray-50">
                <td className="px-4 py-2 text-sm">{index + 1}</td>
                <td className="px-4 py-2 text-sm">{doc.documentName}</td>
                <td className="px-4 py-2 text-sm">{doc.fileName}</td>
                <td className="px-4 py-2 text-sm">
                  {format(new Date(doc.lastUpdated), 'dd-MMM-yyyy')}
                </td>
                <td className="px-4 py-2 text-sm">{doc.comment}</td>
                <td className="px-4 py-2 text-sm">
                  <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                    Uploaded
                  </span>
                </td>
              </tr>
            ))}
            {uploadedDocuments.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">
                  No documents uploaded yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
  // -------------------------------------------------------------

  // --------------------- HANDLERS ---------------------
  const handleInputChange = (section, subsection, field, value) => {
    setDeclarationData((prev) => ({
      ...prev,
      [section]: subsection
        ? {
            ...prev[section],
            [subsection]: {
              ...prev[section][subsection],
              [field]: value,
            },
          }
        : {
            ...prev[section],
            [field]: value,
          },
    }));

    // Inline validation
    if (section === 'section80C') {
      const labelMap = {
        lifeInsurance: 'Life Insurance',
        ppf: 'PPF',
        vpf: 'VPF',
        pensionFund: 'Pension Fund',
        nsc: 'NSC',
        nscInterest: 'NSC Interest',
        ulip: 'ULIP',
        elss: 'ELSS',
        tuitionFees: 'Tuition Fees',
        houseLoanPrincipal: 'House Loan Principal',
        registrationCharges: 'Registration Charges',
        sukanyaSamriddhi: 'Sukanya Samriddhi',
        infrastructureBonds: 'Infrastructure Bonds',
        fixedDeposit: 'Fixed Deposit',
      };
      if (labelMap[field]) {
        setFieldError(field, getNumberRangeError(labelMap[field], value));
      }
    }

    if (section === 'sectionVIA' && subsection === 'section80D') {
      const labelMap = {
        self: 'Self Medical Insurance',
        parents: 'Parents Medical Insurance',
        preventiveHealthCheckup: 'Preventive Health Checkup',
        parentsSenior: 'Senior Citizen Parents Medical Insurance',
      };
      const idMap = {
        self: 'section80DSelf',
        parents: 'section80DParents',
        preventiveHealthCheckup: 'preventiveHealthCheckup',
        parentsSenior: 'section80DParentsSenior',
      };
      if (labelMap[field]) {
        setFieldError(idMap[field], getNumberRangeError(labelMap[field], value));
      }
    }

    if (section === 'sectionVIA' && subsection === 'otherInvestments') {
      const labelMap = {
        section80E: 'Interest Education Loan',
        section80CCD1B: 'NPS Additional Contribution',
        section80EE: 'Interest on Home Loan 80EE',
        section80G: 'Donations 80G',
        section80TTA: 'Interest on Savings 80TTA',
        section80TTB: 'Interest on Deposits 80TTB',
      };
      if (labelMap[field]) {
        setFieldError(field, getNumberRangeError(labelMap[field], value));
      }
    }
  };

  // --- HRA (multiple entries) helpers ---

  const addHraEntry = () => {
    setDeclarationData((prev) => ({
      ...prev,
      hraEntries: [
        ...((Array.isArray(prev.hraEntries) ? prev.hraEntries : [])),
        {
          cityType: 'metro',
          totalRentPaid: '',
          landlordPan: '',
          rentalAddress: '',
        },
      ],
    }));
  };

  const removeHraEntry = (index) => {
    setDeclarationData((prev) => {
      const current = Array.isArray(prev.hraEntries) ? prev.hraEntries : [];
      const next = current.filter((_, i) => i !== index);
      return {
        ...prev,
        hraEntries: next.length
          ? next
          : [
              {
                cityType: 'metro',
                totalRentPaid: '',
                landlordPan: '',
                rentalAddress: '',
              },
            ],
      };
    });
  };

  const handleHraChange = (index, field, value) => {
    // Normalize PAN to uppercase and limit to 10 characters
    let nextValue = value;
    if (field === 'landlordPan' && typeof value === 'string') {
      nextValue = value.toUpperCase().slice(0, 10); // Limit to 10 characters
    }

    setDeclarationData((prev) => {
      const updatedEntries = (Array.isArray(prev.hraEntries) ? prev.hraEntries : []).map((item, i) =>
        i === index ? { ...item, [field]: nextValue } : item
      );
      
      // Validate after state update using the updated entry
      const updatedRow = updatedEntries[index];
      
      if (field === 'totalRentPaid') {
        const amount = Number(value) || 0;
        if (amount > 100000) {
          const landlordPan = (updatedRow?.landlordPan || '').trim();
          if (!landlordPan) {
            setFieldError(
              `hra-landlordPan-${index}`,
              `Landlord PAN is mandatory when Total Rent Paid is more than ₹1,00,000`
            );
          } else {
            // Still validate PAN format if provided
            const panValidationErr = getPanError(`Landlord PAN`, landlordPan);
            if (panValidationErr) {
              setFieldError(`hra-landlordPan-${index}`, panValidationErr);
            }
          }
        }
      }
      
      if (field === 'landlordPan') {
        const amount = Number(updatedRow?.totalRentPaid) || 0;
        if (amount > 100000 && !nextValue.trim()) {
          setFieldError(
            `hra-landlordPan-${index}`,
            `Landlord PAN is mandatory when Total Rent Paid is more than ₹1,00,000`
          );
        }
      }
      
      return {
        ...prev,
        hraEntries: updatedEntries,
      };
    });

    if (field === 'totalRentPaid') {
      setFieldError(
        `hra-rentPaid-${index}`,
        getNumberRangeError(`Total Rent Paid `, value)
      );
    }
    if (field === 'landlordPan') {
      setFieldError(
        `hra-landlordPan-${index}`,
        getPanError(`Landlord PAN`, nextValue)
      );
    }
    if (field === 'rentalAddress') {
      setFieldError(
        `hra-address-${index}`,
        getAddressError(`Rental Address `, value)
      );
    }
  };

  // --- House Property (multiple entries) helpers ---

  const addHousePropertyEntry = () => {
    const current = declarationData.housePropertyEntries || [];
    if (current.length >= MAX_HOUSE_PROPERTIES) {
      toast.error(`At most ${MAX_HOUSE_PROPERTIES} house properties can be declared.`);
      return;
    }
    setDeclarationData((prev) => ({
      ...prev,
      housePropertyEntries: [
        ...((Array.isArray(prev.housePropertyEntries) ? prev.housePropertyEntries : [])),
        {
          propertyType: 'self-occupied',
          loanTakenBeforeApril1999: false,
          interest: '',
          grossAnnualValue: '',
          municipalTaxes: '',
          address: '',
        },
      ],
    }));
  };

  const removeHousePropertyEntry = (index) => {
    setDeclarationData((prev) => {
      const current = Array.isArray(prev.housePropertyEntries) ? prev.housePropertyEntries : [];
      const next = current.filter((_, i) => i !== index);
      return {
        ...prev,
        housePropertyEntries: next.length
          ? next
          : [
              {
                propertyType: 'self-occupied',
                loanTakenBeforeApril1999: false,
                interest: '',
                grossAnnualValue: '',
                municipalTaxes: '',
                address: '',
              },
            ],
      };
    });
  };

  const handleHousePropertyChange = (index, field, value) => {
    if (field === 'propertyType' && value === 'self-occupied') {
      const rows = declarationData.housePropertyEntries || [];
      const otherSopCount = rows.filter(
        (r, i) => i !== index && r.propertyType === 'self-occupied'
      ).length;
      if (otherSopCount >= MAX_SELF_OCCUPIED_PROPERTIES) {
        toast.error(
          `At most ${MAX_SELF_OCCUPIED_PROPERTIES} self-occupied properties are allowed. Use Deemed Let-Out for additional homes.`
        );
        return;
      }
    }

    setDeclarationData((prev) => ({
      ...prev,
      housePropertyEntries: (Array.isArray(prev.housePropertyEntries) ? prev.housePropertyEntries : []).map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      ),
    }));

    if (field === 'interest') {
      setFieldError(
        `interest-${index}`,
        getNumberRangeError(`Interest Paid on Borrowed Capital `, value)
      );
    }
  };

  // --------------------- SECTION TOTAL HELPERS ---------------------

  const getSection80CTotal = () => {
    const s = declarationData.section80C || {};
    const keys = [
      'lifeInsurance',
      'ppf',
      'vpf',
      'pensionFund',
      'nsc',
      'nscInterest',
      'ulip',
      'elss',
      'tuitionFees',
      'houseLoanPrincipal',
      'registrationCharges',
      'sukanyaSamriddhi',
      'infrastructureBonds',
      'fixedDeposit',
    ];
    return keys.reduce((sum, key) => {
      const raw = s[key];
      const num = raw === '' || raw == null ? 0 : Number(raw) || 0;
      return sum + num;
    }, 0);
  };

  const getViaDeductionsTotal = () => {
    if (selectedTaxRegime === 'old') {
      return computeViaDeductionsTotalOldRegime(declarationData.sectionVIA || {});
    }
    return 0;
  };

  const hasAnyDeclaration = () => {
    const s80c = getSection80CTotal();
    const via = getViaDeductionsTotal();
    if (s80c > 0 || via > 0) return true;
    const hasHraData = (declarationData.hraEntries || []).some(
      (e) => (e.totalRentPaid != null && e.totalRentPaid !== '' && Number(e.totalRentPaid)) > 0
    );
    if (hasHraData) return true;
    const hasHpData = (declarationData.housePropertyEntries || []).some(
      (e) => (e.interest != null && e.interest !== '' && Number(e.interest)) > 0
    );
    return hasHpData;
  };

  const handleCancelOrClose = () => {
    // Old cancel popup: no old declaration data (other than prev employment) and currently in old regime
    if (selectedTaxRegime === 'old' && !hasAnyDeclaration()) {
      setCancelConfirmMode('old_nothing_declared');
      return;
    }
    // New cancel popup: there is old regime data and currently in new regime
    if (selectedTaxRegime === 'new' && hasAnyDeclaration()) {
      setCancelConfirmMode('new');
      return;
    }
    onClose();
  };

  // --------------------- RENDER TABS ---------------------
  // 1. Section 80C
  const isReadOnly = mode === 'view' || isInvestmentDeclarationReadOnly();
  // Proof uploads should only be available when declaration itself is editable
  // and when the proof window/settings allow uploads.
  const proofUploadEnabled = !isReadOnly && !isProofUploadReadOnly();
  const taxRegimeChangeAllowed = !isTaxRegimeLocked();

  const renderFieldProof = (sectionName, { isSectionLevel = false } = {}) => {
    if (!proofUploadEnabled || selectedTaxRegime !== 'old') return null;
    const att = getFieldAttachment(sectionName);
    return (
      <SectionUpload
        variant="inline"
        sectionName={sectionName}
        onUpload={handleProofUpload}
        onApprove={handleApproveAttachment}
        onReject={handleRejectAttachment}
        onRemove={handleRemoveAttachment}
        disabled={isReadOnly}
        isSectionLevel={isSectionLevel}
        initialAttachmentId={att?.attachmentId}
        initialFileName={att?.fileName}
        initialFileSize={att?.size}
        initialStatus={att?.status || 'pending'}
        initialRejectReason={att?.comment || ''}
        onViewAttachment={openAttachment}
      />
    );
  };

  const declarationEnabled = !!isInvestmentDeclarationEnabled || !!payrollSettings?.isInvestmentEnabled;
  const proofEnabled = !!isInvestmentProofEnabled || !!payrollSettings?.isProofEnabled;
  const declarationToday = summarizeMonthWindowToday(payrollSettings?.investmentWindows);
  const proofToday = summarizeMonthWindowToday(payrollSettings?.proofWindows);
  const taxYearLockedNow = (() => {
    if (!payrollSettings) return false;
    const lockedList = Array.isArray(payrollSettings.lockedTaxYears)
      ? payrollSettings.lockedTaxYears.map(normalizeFinancialYearKey)
      : [];
    const currentFy = normalizeFinancialYearKey(getEffectiveFinancialYear());
    const activeSettingFy = normalizeFinancialYearKey(payrollSettings.taxYear);
    return (
      (currentFy && lockedList.includes(currentFy)) ||
      (!!payrollSettings.isTaxYearLocked &&
        (!activeSettingFy || financialYearsMatch(currentFy, activeSettingFy)))
    );
  })();

  const accessGuideRows = [
    {
      label: "Declaration",
      open:
        !taxYearLockedNow &&
        (adminBypassWindows ||
          (declarationEnabled && declarationToday.openNow)),
      detail: adminBypassWindows
        ? "Admin edit (bypasses Declaration toggle / day windows)"
        : !declarationEnabled
        ? "Turned off in settings"
        : taxYearLockedNow
        ? "Tax year locked"
        : declarationToday.label,
    },
    {
      label: "Proof upload",
      open:
        adminBypassWindows || (proofEnabled && proofUploadEnabled),
      detail: adminBypassWindows
        ? "Admin edit (bypasses Proof Submission toggle / day windows)"
        : !proofEnabled
        ? "Turned off in settings"
        : proofUploadEnabled
        ? proofToday.label
        : proofToday.openNow
        ? "Not available for you right now"
        : proofToday.label,
    },
    {
      label: "Tax regime switch",
      open: taxRegimeChangeAllowed && !isReadOnly,
      detail: taxRegimeChangeAllowed && !isReadOnly
        ? "You can change once, then it locks"
        : getTaxRegimeLockReason() || "Not changeable right now",
    },
  ];

  const joiningYearNow = isJoiningFinancialYear(
    employeeProfile || employee,
    getEffectiveFinancialYear()
  );
  // Previous employment: only for new joinees in their joining FY (admins may still edit after save)
  const showPrevEmploymentTab =
    joiningYearNow ||
    (isAdminOrHr &&
      (isPrevEmploymentSubmitted ||
        !!(prevEmployment?.employerName || prevEmployment?.employerPan)));

  const isPrevEmploymentReadOnly =
    isReadOnly ||
    (!isAdminOrHr && isPrevEmploymentSubmitted); 
  const renderSection80C = () => {
    const total80C = getSection80CTotal();
    const eligible80C = Math.min(total80C, SECTION_80C_CAP);

    return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-lg font-semibold">Section 80C Deductions</h3>
        <div className="relative" ref={section80CInfoRef}>
          <button
            type="button"
            onClick={() => setShow80CInfo((v) => !v)}
            className={`inline-flex h-8 w-8 items-center justify-center rounded-full border transition-colors ${
              show80CInfo
                ? 'border-redhalo-navy bg-redhalo-mint/40 text-redhalo-navy'
                : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-800'
            }`}
            aria-label="Section 80C rules and eligible amount"
            aria-expanded={show80CInfo}
            title="80C rules and eligible amount"
          >
            <Info className="h-4 w-4" strokeWidth={2.25} />
          </button>
          {show80CInfo && (
            <div className="absolute right-0 top-full z-[10070] mt-2 w-[min(92vw,24rem)] rounded-2xl border border-slate-200 bg-white p-3 shadow-xl">
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="text-sm font-semibold text-slate-900">Section 80C summary</p>
                <button
                  type="button"
                  onClick={() => setShow80CInfo(false)}
                  className="rounded-full p-1 text-slate-400 hover:bg-slate-50 hover:text-slate-700"
                  aria-label="Close"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="mb-3 space-y-1 rounded-xl border border-emerald-100 bg-emerald-50/80 px-2.5 py-2 text-xs font-medium text-emerald-900">
                <p>
                  Total declared:{' '}
                  <span className="tabular-nums">{formatInrCap(total80C)}</span>
                </p>
                <p>
                  Eligible (capped):{' '}
                  <span className="tabular-nums">{formatInrCap(eligible80C)}</span>
                </p>
                <p className="font-normal text-emerald-800/80">
                  Old regime only — overall limit {formatInrCap(SECTION_80C_CAP)}.
                </p>
              </div>
              <div className="space-y-1.5 text-[11px] leading-snug text-slate-600">
                <p className="font-semibold text-slate-800">Rules</p>
                <ul className="list-disc space-y-1 pl-4">
                  <li>
                    Aggregate of all 80C investments is capped at{' '}
                    {formatInrCap(SECTION_80C_CAP)} for the financial year.
                  </li>
                  <li>
                    Employee Provident Fund (EPF) deducted from salary is nested under 80C
                    during TDS and counts toward the same cap.
                  </li>
                  <li>
                    Amounts above the cap do not reduce taxable income further.
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-1">
        {[
          { id: 'lifeInsurance', label: 'Life Insurance Premium', sectionName: 'Life Insurance Premium' },
          { id: 'ppf', label: 'Public Provident Fund (PPF)', sectionName: 'Public Provident Fund (PPF)' },
          { id: 'vpf', label: 'Voluntary Provident Fund (VPF)', sectionName: 'Voluntary Provident Fund (VPF)' },
          { id: 'pensionFund', label: 'Pension Fund Contribution', sectionName: 'Pension Fund Contribution' },
          { id: 'nsc', label: 'National Savings Certificate (NSC)', sectionName: 'National Savings Certificate (NSC)' },
          { id: 'nscInterest', label: 'Interest Accrued on NSC (Reinvested)', sectionName: 'Interest Accrued on NSC (Reinvested)' },
          { id: 'ulip', label: 'Unit Linked Insurance Policy (ULIP)', sectionName: 'Unit Linked Insurance Policy (ULIP)' },
          { id: 'elss', label: 'Equity Linked Savings Schemes (ELSS)', sectionName: 'Equity Linked Savings Schemes (ELSS)' },
          { id: 'tuitionFees', label: 'Payment of Tuition Fees (Max 2 Children)', sectionName: 'Payment of Tuition Fees (Max 2 Children)' },
          { id: 'houseLoanPrincipal', label: 'Principal Repayment of Housing Loan', sectionName: 'Principal Repayment of Housing Loan' },
          { id: 'registrationCharges', label: 'Registration Charges for Buying House (1 Year Only)', sectionName: 'Registration Charges for Buying House' },
          { id: 'sukanyaSamriddhi', label: 'Sukanya Samriddhi Yojana', sectionName: 'Sukanya Samriddhi Yojana' },
          { id: 'infrastructureBonds', label: 'Infrastructure Bonds', sectionName: 'Infrastructure Bonds' },
          { id: 'fixedDeposit', label: 'Bank Fixed Deposit (5+ Years)', sectionName: 'Bank Fixed Deposit (5+ Years)' },
        ].map((field) => (
          <FloatingInput
            key={field.id}
            id={field.id}
            label={field.label}
            type="number"
            value={declarationData.section80C[field.id]}
            onChange={(e) =>
              handleInputChange('section80C', null, field.id, e.target.value)
            }
            disabled={isReadOnly}
            error={errors[field.id]}
            rightSlot={renderFieldProof(field.sectionName)}
          />
        ))}
      </div>

      {!isReadOnly && proofUploadEnabled && selectedTaxRegime === 'old' && (
        <div className="mt-2">
          <BulkActions
            sectionName="Section 80C"
            onBulkAction={handleBulkAction}
            disabled={isReadOnly}
          />
        </div>
      )}
    </div>
  );
  };

  // 2. HRA (House Rent Allowance) - Separate Tab (multiple entries)
  const renderHRA = () => {
    const hraEntries = declarationData.hraEntries || [];
    const declaredRent = (hraEntries || []).reduce(
      (sum, e) => sum + (Number(e.totalRentPaid) || 0),
      0
    );
    const hasMetro = (hraEntries || []).some((e) => e.cityType === 'metro');
    const hasNonMetro = (hraEntries || []).some((e) => e.cityType === 'non-metro');
    // Match TDS: any metro entry makes the aggregate metro
    const isMetro = hasMetro || !hasNonMetro;
    const cityLabel =
      hasMetro && hasNonMetro
        ? 'Mixed (metro & non-metro entries)'
        : hasMetro
          ? 'Metro (50% of basic)'
          : hasNonMetro
            ? 'Non-metro (40% of basic)'
            : 'Not set';
    const hraDetail = computeHraExemptionDetail({
      hraAnnual: salaryTaxBases.hraAnnual,
      basicAnnual: salaryTaxBases.basicAnnual,
      rentAnnual: declaredRent,
      isMetro,
    });

    return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-lg font-semibold">House Rent Allowance (HRA)</h3>
        <div className="relative" ref={hraInfoRef}>
          <button
            type="button"
            onClick={() => setShowHraInfo((v) => !v)}
            className={`inline-flex h-8 w-8 items-center justify-center rounded-full border transition-colors ${
              showHraInfo
                ? 'border-redhalo-navy bg-redhalo-mint/40 text-redhalo-navy'
                : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-800'
            }`}
            aria-label="HRA exemption calculation and rules"
            aria-expanded={showHraInfo}
            title="HRA exemption calculation and rules"
          >
            <Info className="h-4 w-4" strokeWidth={2.25} />
          </button>
          {showHraInfo && (
            <div className="absolute right-0 top-full z-[10070] mt-2 w-[min(92vw,24rem)] rounded-2xl border border-slate-200 bg-white p-3 shadow-xl">
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="text-sm font-semibold text-slate-900">
                  HRA exemption (u/s 10(13A))
                </p>
                <button
                  type="button"
                  onClick={() => setShowHraInfo(false)}
                  className="rounded-full p-1 text-slate-400 hover:bg-slate-50 hover:text-slate-700"
                  aria-label="Close"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>

              <div className="mb-3 space-y-1.5 rounded-xl border border-slate-100 bg-slate-50/80 px-2.5 py-2 text-[11px] leading-snug text-slate-600">
                <p className="font-semibold text-slate-800">How exemption is calculated</p>
                <p>
                  Exempt HRA = minimum of the following three amounts (Old regime only):
                </p>
                <ul className="list-disc space-y-1 pl-4">
                  <li>Actual HRA received for the year (from salary)</li>
                  <li>Rent paid − 10% of basic salary</li>
                  <li>
                    50% of basic salary (metro) or 40% of basic salary (non-metro)
                  </li>
                </ul>
                <p className="pt-1 text-slate-500">
                  Landlord PAN is required when annual rent exceeds ₹1,00,000.
                </p>
              </div>

              <div className="mb-3 space-y-1 rounded-xl border border-emerald-100 bg-emerald-50/80 px-2.5 py-2 text-xs text-emerald-900">
                {salaryTaxBases.loading ? (
                  <p className="text-emerald-800/80">Loading salary Basic / HRA…</p>
                ) : (
                  <>
                    <p className="font-semibold text-emerald-950">
                      Estimated exemption:{' '}
                      <span className="tabular-nums">{formatInrCap(hraDetail.exemption)}</span>
                    </p>
                    <p className="font-normal text-[11px] leading-snug text-emerald-800/90">
                      Actual HRA (salary):{' '}
                      <span className="tabular-nums">{formatInrCap(hraDetail.actualHra)}</span>
                      {' · '}Basic (salary):{' '}
                      <span className="tabular-nums">{formatInrCap(hraDetail.basic)}</span>
                    </p>
                    <p className="font-normal text-[11px] leading-snug text-emerald-800/90">
                      Rent − 10% basic:{' '}
                      <span className="tabular-nums">
                        {formatInrCap(hraDetail.rentMinus10PctBasic)}
                      </span>
                      {' · '}
                      {hraDetail.metroPct}% of basic:{' '}
                      <span className="tabular-nums">{formatInrCap(hraDetail.pctOfBasic)}</span>
                    </p>
                    {!salaryTaxBases.loaded && (
                      <p className="pt-0.5 text-[10px] font-normal text-amber-800">
                        Salary Basic/HRA not loaded — exemption may be ₹0 until liability data is
                        available.
                      </p>
                    )}
                  </>
                )}
              </div>

              <div className="space-y-1.5 text-xs text-slate-700">
                <p>
                  Declared rent (FY):{' '}
                  <span className="font-medium tabular-nums">
                    {formatInrCap(declaredRent)}
                  </span>
                </p>
                <p>
                  City type:{' '}
                  <span className="font-medium">{cityLabel}</span>
                </p>
                <p className="text-[11px] text-slate-500">
                  Updates live as you change rent or metro / non-metro. Final TDS uses the same
                  formula with payroll Basic + HRA.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {hraEntries.map((entry, index) => (
        <div key={index} className="p-4 bg-gray-50 mb-4">
          <div className="space-y-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* City Type Radio Buttons */}
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name={`hra-cityType-${index}`}
                  value="metro"
                  checked={entry.cityType === 'metro'}
                  onChange={(e) => handleHraChange(index, 'cityType', e.target.value)}
                  disabled={isReadOnly}
                  className="mr-2"
                />
                Metro
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name={`hra-cityType-${index}`}
                  value="non-metro"
                  checked={entry.cityType === 'non-metro'}
                  onChange={(e) => handleHraChange(index, 'cityType', e.target.value)}
                  disabled={isReadOnly}
                  className="mr-2"
                />
                Non-Metro
              </label>
            </div>

            <FloatingInput
              id={`hra-rentPaid-${index}`}
              label="Total Rent Paid/Payable"
              type="number"
              value={entry.totalRentPaid}
              onChange={(e) => handleHraChange(index, 'totalRentPaid', e.target.value)}
              disabled={isReadOnly}
              error={errors[`hra-rentPaid-${index}`]}
              rightSlot={renderFieldProof(`HRA - Entry ${index + 1}`, {
                isSectionLevel: true,
              })}
            />
            <FloatingInput
              id={`hra-landlordPan-${index}`}
              label="Landlord PAN"
              value={entry.landlordPan || ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value.length <= 10) {
                  handleHraChange(index, 'landlordPan', value);
                }
              }}
              disabled={isReadOnly}
              error={errors[`hra-landlordPan-${index}`]}
              maxLength={10}
            />
            <FloatingInput
              id={`hra-address-${index}`}
              label="Rental Address"
              value={entry.rentalAddress}
              onChange={(e) => handleHraChange(index, 'rentalAddress', e.target.value)}
              disabled={isReadOnly}
              error={errors[`hra-address-${index}`]}
            />
          </div>

          {!isReadOnly && index > 0 && (
            <button
              type="button"
              className="mt-2 text-red-600 hover:text-red-800 text-sm"
              onClick={() => removeHraEntry(index)}
            >
              Remove HRA
            </button>
          )}
        </div>
      ))}

      {/* {!isReadOnly && (
        <button
          type="button"
          onClick={addHraEntry}
          className="m-2 text-redhalo-navy hover:text-redhalo-navy-dark text-sm"
        >
          + Add HRA
        </button>
      )} */}
    </div>
  );
  };

  // 3. Section 80D - Medical Insurance Only
  const renderSection80D = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold mb-4">Section 80D - Medical Insurance</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* 80D */}
        <FloatingInput
          id="section80DSelf"
          label="Self Medical Insurance (80D)"
          type="number"
          value={declarationData.sectionVIA.section80D.self}
          onChange={(e) => handleInputChange('sectionVIA', 'section80D', 'self', e.target.value)}
          disabled={isReadOnly}
          error={errors.section80DSelf}
        />
        <FloatingInput
          id="section80DParents"
          label="Parents Medical Insurance (80D)"
          type="number"
          value={declarationData.sectionVIA.section80D.parents}
          onChange={(e) => handleInputChange('sectionVIA', 'section80D', 'parents', e.target.value)}
          disabled={isReadOnly}
          error={errors.section80DParents}
        />

        {/* Senior Citizen Checkboxes */}
        <div className="flex items-center mb-4">
          <input
            type="checkbox"
            id="selfSeniorCitizen"
            checked={declarationData.sectionVIA.section80D.isSelfSeniorCitizen}
            onChange={(e) =>
              handleInputChange('sectionVIA', 'section80D', 'isSelfSeniorCitizen', e.target.checked)
            }
            className="mr-2"
            disabled={isReadOnly}
          />
          <label htmlFor="selfSeniorCitizen">Self Senior Citizen</label>
        </div>
        <div className="flex items-center mb-4">
          <input
            type="checkbox"
            id="parentsSeniorCitizen"
            checked={declarationData.sectionVIA.section80D.isParentsSeniorCitizen}
            onChange={(e) =>
              handleInputChange(
                'sectionVIA',
                'section80D',
                'isParentsSeniorCitizen',
                e.target.checked,
              )
            }
            className="mr-2"
            disabled={isReadOnly}
          />
          <label htmlFor="parentsSeniorCitizen">Parents Senior Citizen</label>
        </div>

        <FloatingInput
          id="preventiveHealthCheckup"
          label="Preventive Health Checkup (80D)"
          type="number"
          value={declarationData.sectionVIA.section80D.preventiveHealthCheckup}
          onChange={(e) =>
            handleInputChange('sectionVIA', 'section80D', 'preventiveHealthCheckup', e.target.value)
          }
          disabled={isReadOnly}
          error={errors.preventiveHealthCheckup}
        />
      </div>
    </div>
  );

  // 4. VIA-deductions (Combined Section 80D and Other Investments)
  const renderVIADeductions = () => {
    const d80 = declarationData.sectionVIA?.section80D || {};
    const selfCap = medicalCapForCategory(!!d80.isSelfSeniorCitizen);
    const parentsCap = medicalCapForCategory(!!d80.isParentsSeniorCitizen);
    const allowed80D = computeSection80DAllowedFromUi(d80);

    return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] overflow-hidden shadow-[var(--ds-shadow-card)]">
          <div className="flex items-center justify-between gap-2 border-b border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]/40 px-4 py-3">
            <h3 className="text-base font-semibold text-slate-900">
              Section 80D — Medical Insurance
            </h3>
            <div className="relative" ref={section80DInfoRef}>
              <button
                type="button"
                onClick={() => setShow80DInfo((v) => !v)}
                className={`inline-flex h-8 w-8 items-center justify-center rounded-full border transition-colors ${
                  show80DInfo
                    ? 'border-redhalo-navy bg-redhalo-mint/40 text-redhalo-navy'
                    : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-800'
                }`}
                aria-label="Section 80D rules and eligible amount"
                aria-expanded={show80DInfo}
                title="80D rules and eligible amount"
              >
                <Info className="h-4 w-4" strokeWidth={2.25} />
              </button>
              {show80DInfo && (
                <div className="absolute right-0 top-full z-[10070] mt-2 w-[min(92vw,22rem)] rounded-2xl border border-slate-200 bg-white p-3 shadow-xl">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold text-slate-900">Section 80D summary</p>
                    <button
                      type="button"
                      onClick={() => setShow80DInfo(false)}
                      className="rounded-full p-1 text-slate-400 hover:bg-slate-50 hover:text-slate-700"
                      aria-label="Close"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <p className="mb-3 rounded-xl border border-emerald-100 bg-emerald-50/80 px-2.5 py-2 text-xs font-medium text-emerald-900">
                    Eligible 80D (capped): {formatInrCap(allowed80D)}
                    <span className="mt-0.5 block font-normal text-emerald-800/80">
                      Old regime only — not allowed under New regime.
                    </span>
                  </p>
                  <div className="space-y-1.5 text-[11px] leading-snug text-slate-600">
                    <p className="font-semibold text-slate-800">Rules</p>
                    <ul className="list-disc space-y-1 pl-4">
                      <li>
                        Self / spouse / dependent children: limit {formatInrCap(selfCap)}
                        {d80.isSelfSeniorCitizen ? ' (senior 60+)' : ' (below 60)'}.
                      </li>
                      <li>
                        Parents: separate limit {formatInrCap(parentsCap)}
                        {d80.isParentsSeniorCitizen ? ' (senior 60+)' : ' (below 60)'}.
                      </li>
                      <li>
                        Preventive health check-up: max ₹5,000 within each category&apos;s overall
                        cap (not over and above).
                      </li>
                      <li>
                        Senior citizen (60+) raises that category&apos;s medical insurance cap to
                        ₹50,000.
                      </li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="divide-y divide-[hsl(var(--ds-border))]">
            <div className="space-y-3 px-4 py-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-semibold text-slate-800">
                  Self, Spouse &amp; Dependent Children
                  <span className="ml-2 font-normal text-slate-500">
                    (Limit {formatInrCap(selfCap)}
                    {d80.isSelfSeniorCitizen ? ', senior 60+' : ', below 60'})
                  </span>
                </p>
                <label
                  htmlFor="selfSeniorCitizenVia"
                  className="inline-flex items-center gap-1.5 text-sm text-slate-700"
                >
                  <input
                    type="checkbox"
                    id="selfSeniorCitizenVia"
                    checked={!!d80.isSelfSeniorCitizen}
                    onChange={(e) =>
                      handleInputChange(
                        'sectionVIA',
                        'section80D',
                        'isSelfSeniorCitizen',
                        e.target.checked
                      )
                    }
                    disabled={isReadOnly}
                  />
                  Senior citizen (60+ years)
                </label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FloatingInput
                  id="section80DSelf"
                  label="Medical insurance premium"
                  type="number"
                  value={d80.self}
                  onChange={(e) =>
                    handleInputChange('sectionVIA', 'section80D', 'self', e.target.value)
                  }
                  disabled={isReadOnly}
                  error={errors.section80DSelf}
                  rightSlot={renderFieldProof('Section 80D - Medical Insurance', {
                    isSectionLevel: true,
                  })}
                />
                <FloatingInput
                  id="selfPreventiveHealthCheckup"
                  label="Preventive health check-up (max ₹5,000 in category cap)"
                  type="number"
                  value={d80.selfPreventiveHealthCheckup}
                  onChange={(e) =>
                    handleInputChange(
                      'sectionVIA',
                      'section80D',
                      'selfPreventiveHealthCheckup',
                      e.target.value
                    )
                  }
                  disabled={isReadOnly}
                  error={errors.selfPreventiveHealthCheckup}
                />
              </div>
            </div>

            <div className="space-y-3 px-4 py-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-semibold text-slate-800">
                  Parents
                  <span className="ml-2 font-normal text-slate-500">
                    (Limit {formatInrCap(parentsCap)}
                    {d80.isParentsSeniorCitizen ? ', senior 60+' : ', below 60'})
                  </span>
                </p>
                <label
                  htmlFor="parentsSeniorCitizenVia"
                  className="inline-flex items-center gap-1.5 text-sm text-slate-700"
                >
                  <input
                    type="checkbox"
                    id="parentsSeniorCitizenVia"
                    checked={!!d80.isParentsSeniorCitizen}
                    onChange={(e) =>
                      handleInputChange(
                        'sectionVIA',
                        'section80D',
                        'isParentsSeniorCitizen',
                        e.target.checked
                      )
                    }
                    disabled={isReadOnly}
                  />
                  Senior citizen (60+ years)
                </label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FloatingInput
                  id="section80DParents"
                  label="Medical insurance premium"
                  type="number"
                  value={d80.parents}
                  onChange={(e) =>
                    handleInputChange('sectionVIA', 'section80D', 'parents', e.target.value)
                  }
                  disabled={isReadOnly}
                  error={errors.section80DParents}
                />
                <FloatingInput
                  id="parentsPreventiveHealthCheckup"
                  label="Preventive health check-up (max ₹5,000 in category cap)"
                  type="number"
                  value={d80.parentsPreventiveHealthCheckup}
                  onChange={(e) =>
                    handleInputChange(
                      'sectionVIA',
                      'section80D',
                      'parentsPreventiveHealthCheckup',
                      e.target.value
                    )
                  }
                  disabled={isReadOnly}
                  error={errors.parentsPreventiveHealthCheckup}
                />
              </div>
            </div>
          </div>
        </div>
    </div>

      {/* Other Investments */}
    <div className="space-y-4">
      <h3 className="text-lg font-semibold mb-4">Other Investments</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-1">
        {[
          {
            id: 'section80E',
            label: 'Interest on Education Loan (80E)',
            sectionName: 'Interest on Education Loan (80E)',
          },
          {
            id: 'section80CCD1B',
            label: 'NPS Additional Contribution (80CCD(1B))',
            sectionName: 'NPS Additional Contribution (80CCD(1B))',
          },
          {
            id: 'section80EE',
            label: 'Interest on Home Loan (80EE)',
            sectionName: 'Interest on Home Loan (80EE)',
          },
          {
            id: 'section80EEA',
            label: 'Interest on Home Loan (80EEA)',
            sectionName: 'Interest on Home Loan (80EEA)',
          },
          {
            id: 'section80EEB',
            label: 'Interest on Home Loan (80EEB)',
            sectionName: 'Interest on Home Loan (80EEB)',
          },
          {
            id: 'section80TTA',
            label: 'Interest on Savings Account (80TTA)',
            sectionName: 'Interest on Savings Account (80TTA)',
          },
        ].map((field) => (
          <FloatingInput
            key={field.id}
            id={field.id}
            label={field.label}
            type="number"
            value={declarationData.sectionVIA.otherInvestments[field.id]}
            onChange={(e) =>
              handleInputChange(
                'sectionVIA',
                'otherInvestments',
                field.id,
                e.target.value
              )
            }
            disabled={isReadOnly}
            error={errors[field.id]}
            rightSlot={renderFieldProof(field.sectionName)}
          />
        ))}
      </div>
      {!isReadOnly && proofUploadEnabled && selectedTaxRegime === 'old' && (
        <div className="mt-2">
          <BulkActions
            sectionName="Other Investments"
            onBulkAction={handleBulkAction}
            disabled={isReadOnly}
          />
        </div>
      )}
    </div>
    </div>
  );
  };
  const renderPreviousEmployment = () => {
    const sectionReadOnly = isPrevEmploymentReadOnly;
    const isRequired = requiresPrevEmploymentDetails();

    return (
      <div className="space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <h3 className="text-lg font-semibold text-slate-900">Previous Employment</h3>
          {isPrevEmploymentSubmitted && !isAdminOrHr ? (
            <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-[11px] font-medium text-amber-800">
              Saved — contact Payroll to change
            </span>
          ) : null}
          {isPrevEmploymentSubmitted && isAdminOrHr ? (
            <span className="rounded-full border border-blue-200 bg-blue-50 px-2.5 py-0.5 text-[11px] font-medium text-blue-800">
              Admin can edit
            </span>
          ) : null}
        </div>
        <p className="text-sm text-slate-600">
          For new joinees in their joining year only (once in a lifetime).
          {!isRequired
            ? " Optional if you have no prior employment — leave blank to save as Not Applicable."
            : " Enter tax details from your previous employer."}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FloatingInput
            id="prevEmployerName"
            label="Previous Employer Name"
            value={prevEmployment.employerName}
            error={errors.prevEmployerName}
            onChange={(e) => {
              const value = e.target.value;
              // Only allow letters and spaces
              if (value === '' || /^[A-Za-z\s]+$/.test(value)) {
              setPrevEmployment((prev) => ({
                ...prev,
                  employerName: value,
                }));
                // Clear error when user types (validation will happen on Save & Next)
                if (errors.prevEmployerName) {
                  setFieldError('prevEmployerName', '');
                }
            }
            }}
            disabled={sectionReadOnly}
            required={isRequired}
          />

          <FloatingInput
            id="prevEmployerPan"
            label="PAN (e.g. ABCDE1234F)"
            value={prevEmployment.employerPan}
            error={errors.prevEmployerPan}
            onChange={(e) => {
              const raw = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
              setPrevEmployment((prev) => ({ ...prev, employerPan: raw }));
              if (errors.prevEmployerPan) setFieldError('prevEmployerPan', '');
              const panErr = getPanError('PAN', raw);
              if (raw && panErr) setFieldError('prevEmployerPan', panErr);
            }}
            disabled={sectionReadOnly}
            required={isRequired}
          />

          <FloatingInput
            id="prevEmployerTan"
            label="TAN (e.g. ABCD12345E)"
            value={prevEmployment.employerTan}
            error={errors.prevEmployerTan}
            onChange={(e) => {
              const raw = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10);
              setPrevEmployment((prev) => ({ ...prev, employerTan: raw }));
              if (errors.prevEmployerTan) setFieldError('prevEmployerTan', '');
              const tanErr = getTanError('TAN', raw);
              if (raw && tanErr) setFieldError('prevEmployerTan', tanErr);
            }}
            disabled={sectionReadOnly}
            required={false}
          />

          <FloatingInput
            id="prevGrossSalary"
            label="Total Salary Received (Gross)"
            type="number"
            value={prevEmployment.grossSalary}
            error={errors.prevGrossSalary}
            onChange={(e) =>
              setPrevEmployment((prev) => {
                const nextValue = e.target.value;
                // Clear error when user types (validation will happen on Save & Next)
                if (errors.prevGrossSalary) {
                  setFieldError('prevGrossSalary', '');
                }
                // Clear related tax error if it was about comparison
                if (errors.prevTaxDeducted && errors.prevTaxDeducted.includes('must be less than')) {
                  setFieldError('prevTaxDeducted', '');
                }
                return {
                  ...prev,
                  grossSalary: nextValue,
                };
              })
            }
            disabled={sectionReadOnly}
            required={isRequired}
          />

          <FloatingInput
            id="prevTaxDeducted"
            label="Total Tax Deducted"
            type="number"
            value={prevEmployment.taxDeducted}
            error={errors.prevTaxDeducted}
            onChange={(e) =>
              setPrevEmployment((prev) => {
                const nextValue = e.target.value;
                // Clear error when user types (validation will happen on Save & Next)
                if (errors.prevTaxDeducted) {
                  setFieldError('prevTaxDeducted', '');
                }
                return {
                  ...prev,
                  taxDeducted: nextValue,
                };
              })
            }
            disabled={sectionReadOnly}
            required={isRequired}
          />

          <FloatingInput
            id="prevProfessionalTax"
            label="Professional Tax Deducted (Previous Employer)"
            type="number"
            value={prevEmployment.professionalTax}
            error={errors.prevProfessionalTax}
            onChange={(e) =>
              setPrevEmployment((prev) => {
                const nextValue = e.target.value;
                if (errors.prevProfessionalTax) {
                  setFieldError('prevProfessionalTax', '');
                }
                return {
                  ...prev,
                  professionalTax: nextValue,
                };
              })
            }
            disabled={sectionReadOnly}
            required={false}
          />
        </div>
      </div>
    );
  };

  // 4. House Property (multiple entries)
  const renderHousePropertyDetails = () => {
    const housePropertyEntries = declarationData.housePropertyEntries || [];
    const hpAgg = computeHousePropertyAggregateFromUi(housePropertyEntries);

    return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-base font-semibold">House Property Details</h3>
        <div className="relative" ref={hpAggInfoRef}>
          <button
            type="button"
            onClick={() => setShowHpAggInfo((v) => !v)}
            className={`inline-flex h-8 w-8 items-center justify-center rounded-full border transition-colors ${
              showHpAggInfo
                ? 'border-redhalo-navy bg-redhalo-mint/40 text-redhalo-navy'
                : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-800'
            }`}
            aria-label="House property aggregate and rules"
            aria-expanded={showHpAggInfo}
            title="FY aggregate computation and rules"
          >
            <Info className="h-4 w-4" strokeWidth={2.25} />
          </button>
          {showHpAggInfo && (
            <div className="absolute right-0 top-full z-[10070] mt-2 w-[min(92vw,24rem)] rounded-2xl border border-slate-200 bg-white p-3 shadow-xl">
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="text-sm font-semibold text-slate-900">
                  FY aggregate (Old regime)
                </p>
                <button
                  type="button"
                  onClick={() => setShowHpAggInfo(false)}
                  className="rounded-full p-1 text-slate-400 hover:bg-slate-50 hover:text-slate-700"
                  aria-label="Close"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>

              <div className="mb-3 space-y-1.5 rounded-xl border border-slate-100 bg-slate-50/80 px-2.5 py-2 text-[11px] leading-snug text-slate-600">
                <p className="font-semibold text-slate-800">Rules</p>
                <ul className="list-disc space-y-1 pl-4">
                  <li>
                    Up to {MAX_SELF_OCCUPIED_PROPERTIES} self-occupied properties (SOP).
                  </li>
                  <li>
                    SOP: GAV is nil. Combined interest u/s 24(b) capped at{' '}
                    {formatInrCap(SELF_OCCUPIED_HP_INTEREST_CAP)}.
                  </li>
                  <li>
                    LOP / DLOP: GAV − municipal taxes − 30% std. deduction − interest (no
                    interest cap on the property).
                  </li>
                  <li>
                    Aggregate HP loss set-off against salary / other income capped at{' '}
                    {formatInrCap(HOUSE_PROPERTY_LOSS_SETOFF_CAP)} (s.71(3A)). Excess is
                    carried forward (set off later only against HP income).
                  </li>
                </ul>
              </div>

              {housePropertyEntries.length === 0 ? (
                <p className="text-xs text-slate-500">No house property entries yet.</p>
              ) : (
                <div className="space-y-1.5 text-xs text-slate-700">
                  {housePropertyEntries.map((entry, idx) => {
                    if (!isRentablePropertyType(entry.propertyType)) return null;
                    const b = computeRentablePropertyBreakdown(entry);
                    const typeLabel =
                      entry.propertyType === 'deemed-let-out'
                        ? 'Deemed let-out'
                        : 'Let-out';
                    return (
                      <p key={`hp-lop-${idx}`}>
                        Property {idx + 1} ({typeLabel}): NAV{' '}
                        <span className="font-medium tabular-nums">
                          {formatInrCap(b.nav)}
                        </span>
                        {' · '}30% std ded{' '}
                        <span className="font-medium tabular-nums">
                          {formatInrCap(b.standardDeduction)}
                        </span>
                        {' · '}Net{' '}
                        <span
                          className={`font-medium tabular-nums ${
                            b.netIncome < 0 ? 'text-red-700' : 'text-emerald-700'
                          }`}
                        >
                          {formatInrCap(b.netIncome)}
                          {b.netIncome < 0 ? ' (loss)' : ' (profit)'}
                        </span>
                      </p>
                    );
                  })}
                  <p>
                    Self-occupied interest declared:{' '}
                    <span className="font-medium tabular-nums">
                      {formatInrCap(hpAgg.sopInterestTotal)}
                    </span>
                    {' · '}Allowed:{' '}
                    <span className="font-medium tabular-nums">
                      {formatInrCap(hpAgg.sopAllowedInterest)}
                    </span>
                    {hpAgg.sopExcessInterest > 0
                      ? ` · Excess not allowed: ${formatInrCap(hpAgg.sopExcessInterest)}`
                      : ''}
                  </p>
                  <p>
                    Raw net from all properties:{' '}
                    <span
                      className={`font-medium tabular-nums ${
                        hpAgg.rawAggregateNet < 0 ? 'text-red-700' : 'text-emerald-700'
                      }`}
                    >
                      {formatInrCap(hpAgg.rawAggregateNet)}
                    </span>
                  </p>
                  {hpAgg.lossSetOffApplied > 0 && (
                    <p>
                      Loss set-off applied (max{' '}
                      {formatInrCap(HOUSE_PROPERTY_LOSS_SETOFF_CAP)}):{' '}
                      <span className="font-medium tabular-nums">
                        {formatInrCap(hpAgg.lossSetOffApplied)}
                      </span>
                      {hpAgg.lossCarriedForward > 0
                        ? ` · Carried forward: ${formatInrCap(hpAgg.lossCarriedForward)}`
                        : ''}
                    </p>
                  )}
                  <p className="font-semibold text-slate-900">
                    Taxable HP adjustment:{' '}
                    <span className="tabular-nums">{formatInrCap(hpAgg.hpNetForTax)}</span>
                    {hpAgg.hpNetForTax < 0
                      ? ' (reduces taxable income)'
                      : hpAgg.hpNetForTax > 0
                        ? ' (increases taxable income)'
                        : ''}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {errors.housePropertyAggregate && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
          {errors.housePropertyAggregate}
        </div>
      )}
      
      {housePropertyEntries.map((entry, index) => (
        <div key={index} className="p-2.5 bg-gray-50 mb-2 rounded-lg border border-slate-200">
          <p className="mb-1 text-sm font-semibold text-slate-800">
            Property {index + 1}
          </p>
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 mb-1.5 text-xs">
            <label className="flex items-center gap-1">
              <input
                type="radio"
                name={`propertyType-${index}`}
                value="self-occupied"
                checked={entry.propertyType === 'self-occupied'}
                onChange={(e) => handleHousePropertyChange(index, 'propertyType', e.target.value)}
                disabled={isReadOnly}
              />
              Self-Occupied (SOP)
            </label>
            <label className="flex items-center gap-1">
              <input
                type="radio"
                name={`propertyType-${index}`}
                value="let-out"
                checked={entry.propertyType === 'let-out'}
                onChange={(e) => handleHousePropertyChange(index, 'propertyType', e.target.value)}
                disabled={isReadOnly}
              />
              Let-Out (LOP)
            </label>
            <label className="flex items-center gap-1">
              <input
                type="radio"
                name={`propertyType-${index}`}
                value="deemed-let-out"
                checked={entry.propertyType === 'deemed-let-out'}
                onChange={(e) => handleHousePropertyChange(index, 'propertyType', e.target.value)}
                disabled={isReadOnly}
              />
              Deemed Let-Out (DLOP)
            </label>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-1.5 items-end">
            <div className="lg:col-span-2 flex items-center min-h-[34px] pb-0.5">
              <input
                type="checkbox"
                id={`loanTakenBeforeApril1999-${index}`}
                checked={entry.loanTakenBeforeApril1999}
                onChange={(e) =>
                  handleHousePropertyChange(
                    index,
                    'loanTakenBeforeApril1999',
                    e.target.checked,
                  )
                }
                className="mr-1.5 shrink-0"
                disabled={isReadOnly}
              />
              <label
                htmlFor={`loanTakenBeforeApril1999-${index}`}
                className="text-[11px] leading-tight text-slate-700"
              >
                Loan taken before April 1999
              </label>
            </div>
            <div className="lg:col-span-2">
              <FloatingInput
                compact
                id={`interest-${index}`}
                label="Interest paid (u/s 24b)"
                type="number"
                value={entry.interest}
                onChange={(e) => handleHousePropertyChange(index, 'interest', e.target.value)}
                disabled={isReadOnly}
                error={errors[`interest-${index}`]}
                required
              />
            </div>
            {isRentablePropertyType(entry.propertyType) && (
              <>
                <div className="lg:col-span-2">
                  <FloatingInput
                    compact
                    id={`grossAnnualValue-${index}`}
                    label="Gross Annual Value (GAV)"
                    type="number"
                    value={entry.grossAnnualValue}
                    onChange={(e) =>
                      handleHousePropertyChange(index, 'grossAnnualValue', e.target.value)
                    }
                    disabled={isReadOnly}
                    error={errors[`grossAnnualValue-${index}`]}
                    required
                  />
                </div>
                <div className="lg:col-span-2">
                  <FloatingInput
                    compact
                    id={`municipalTaxes-${index}`}
                    label="Municipal taxes (FY)"
                    type="number"
                    value={entry.municipalTaxes}
                    onChange={(e) =>
                      handleHousePropertyChange(index, 'municipalTaxes', e.target.value)
                    }
                    disabled={isReadOnly}
                    error={errors[`municipalTaxes-${index}`]}
                  />
                </div>
              </>
            )}
            {entry.propertyType === 'self-occupied' && (
              <div className="lg:col-span-3 flex items-center min-h-[34px] pb-0.5 px-0.5 text-[10px] leading-snug text-slate-500">
                GAV nil · combined SOP interest cap {formatInrCap(SELF_OCCUPIED_HP_INTEREST_CAP)}
              </div>
            )}
            <div
              className={
                isRentablePropertyType(entry.propertyType)
                  ? 'lg:col-span-4'
                  : 'lg:col-span-5'
              }
            >
              <FloatingInput
                compact
                id={`propertyAddress-${index}`}
                label="Property Address"
                value={entry.address}
                onChange={(e) => handleHousePropertyChange(index, 'address', e.target.value)}
                disabled={isReadOnly}
                error={errors[`propertyAddress-${index}`]}
                required
                rightSlot={renderFieldProof(`House Property - Entry ${index + 1}`, {
                  isSectionLevel: true,
                })}
              />
            </div>
          </div>

          {!isReadOnly && housePropertyEntries.length > 1 && (
            <button
              type="button"
              className="mt-2 text-red-600 hover:text-red-800 text-sm"
              onClick={() => removeHousePropertyEntry(index)}
            >
              Remove Property
            </button>
          )}
        </div>
      ))}

      {!isReadOnly && housePropertyEntries.length < MAX_HOUSE_PROPERTIES && (
        <button
          type="button"
          onClick={addHousePropertyEntry}
          className="m-2 text-sm font-medium text-[hsl(var(--ds-primary))] hover:underline"
        >
          + Add House Property
        </button>
      )}
    </div>
  );
  };

  // --------------------- TABS DEFINITION ---------------------
  // Same as-of month as Run Payroll / TDS Comparison — never drift to calendar "today".
  const taxPlanningPayMonth = resolveTaxPlanningPayMonth({
    payMonth,
    financialYear: getEffectiveFinancialYear(),
  });

  const renderTaxLiability = () => {
    const empId = resolveEmployeeId(employee);
    if (!empId) {
      return (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-6 text-sm text-amber-900">
          Employee record is required to load tax liability from payroll.
        </div>
      );
    }
    return (
      <div className="space-y-4">
        <div>
          <h3 className="text-base font-semibold text-slate-900">Tax liability illustration</h3>
          <p className="mt-1 text-sm text-slate-600">
            Projected tax and TDS from the same payroll engine used in Run Payroll → Declarations
            &amp; TDS. Use the eye icon for the full computation breakdown. Figures refresh after you
            save declarations or submit proofs.
          </p>
        </div>
        <TaxLiabilityIllustration
          employeeId={empId}
          empCode={employee?.empCode || employee?.employeeId || null}
          employeeName={
            employee?.name ||
            employee?.user?.name ||
            [employee?.firstName, employee?.lastName].filter(Boolean).join(" ") ||
            null
          }
          payMonth={taxPlanningPayMonth}
          refreshKey={illustrationRefreshKey}
          showComputationEye
        />
      </div>
    );
  };

  const tabs = [
    ...(showPrevEmploymentTab
      ? [
          {
            id: 'prevEmployment',
            label: 'Previous Employment',
            content: renderPreviousEmployment,
          },
        ]
      : []),
    { id: 'hra', label: 'HRA', content: renderHRA },
    { id: 'section80C', label: 'Section 80C', content: renderSection80C },
    { id: 'viaDeductions', label: 'VIA-deductions', content: renderVIADeductions },
    { id: 'houseProperty', label: 'House Property', content: renderHousePropertyDetails },
    { id: 'taxLiability', label: 'Tax liability', content: renderTaxLiability },
  ];

  const modalNode = (
    <div
      className="fixed inset-0 z-[10050] flex items-center justify-center overflow-y-auto bg-black/40 p-3 sm:p-5"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tax-planning-title"
    >
      <div
        className="relative flex w-full max-w-[1100px] flex-col overflow-hidden rounded-lg border-2 border-[hsl(var(--ds-primary)/0.35)] bg-white shadow-[var(--ds-shadow-panel)]"
        style={{ height: 'min(92vh, 820px)', maxHeight: 'calc(100vh - 1.5rem)' }}
      >
        <div className="h-1 w-full shrink-0 bg-[hsl(var(--ds-primary))]" />

        {/* Header */}
        <div className="flex flex-shrink-0 items-start justify-between gap-3 border-b border-[hsl(var(--ds-primary)/0.15)] bg-white px-4 py-3.5 sm:px-6">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 id="tax-planning-title" className="text-lg font-semibold tracking-tight text-slate-900">
                {TAX_PLANNING_TITLE}
              </h2>
              <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                FY {getEffectiveFinancialYear() || '—'}
              </span>
              {mode === 'view' || isReadOnly ? (
                <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-[11px] font-medium text-amber-800">
                  {mode === 'view' ? 'View only' : 'Read-only'}
                </span>
              ) : (
                <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-[11px] font-medium text-emerald-800">
                  Editable
                </span>
              )}
              <div className="relative" ref={accessInfoRef}>
                <button
                  type="button"
                  onClick={() => setShowAccessInfo((v) => !v)}
                  className={`inline-flex h-8 w-8 items-center justify-center rounded-full border transition-colors ${
                    showAccessInfo
                      ? 'border-redhalo-navy bg-redhalo-mint/40 text-redhalo-navy'
                      : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-800'
                  }`}
                  aria-label="What can I do today?"
                  aria-expanded={showAccessInfo}
                  title="Today's access from Declaration settings"
                >
                  <Info className="h-4 w-4" strokeWidth={2.25} />
                </button>
                {showAccessInfo && (
                  <div className="absolute left-0 top-full z-[10070] mt-2 w-[min(92vw,20rem)] rounded-2xl border border-slate-200 bg-white p-3 shadow-xl">
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-slate-900">Today&apos;s access</p>
                      <button
                        type="button"
                        onClick={() => setShowAccessInfo(false)}
                        className="rounded-full p-1 text-slate-400 hover:bg-slate-50 hover:text-slate-700"
                        aria-label="Close"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <ul className="space-y-2">
                      {accessGuideRows.map((row) => (
                        <li
                          key={row.label}
                          className="flex items-start gap-2 rounded-xl border border-slate-100 bg-slate-50/80 px-2.5 py-2"
                        >
                          <span
                            className={`mt-0.5 inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                              row.open
                                ? 'bg-emerald-100 text-emerald-800'
                                : 'bg-slate-200 text-slate-600'
                            }`}
                          >
                            {row.open ? 'Open' : 'Closed'}
                          </span>
                          <div className="min-w-0">
                            <p className="text-xs font-semibold text-slate-800">{row.label}</p>
                            <p className="text-[11px] leading-snug text-slate-500">{row.detail}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
            <p className="mt-1 truncate text-sm text-slate-600">
              {[employee?.user?.name, employee?.user?.lastName].filter(Boolean).join(' ') || 'Employee'}
              {employee?.empCode ? ` · ${employee.empCode}` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={handleCancelOrClose}
            className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-900"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Tax Regime strip */}
        <div className="flex-shrink-0 border-b border-slate-200 bg-slate-50/80 px-4 py-3 sm:px-6">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                Tax Regime
              </span>
              <div className="inline-flex overflow-hidden rounded-full border border-slate-300 bg-white shadow-sm">
                <button
                  type="button"
                  disabled={isReadOnly || !taxRegimeChangeAllowed}
                  onClick={() => {
                    if (selectedTaxRegime === 'old' || isReadOnly || !taxRegimeChangeAllowed) return;
                    saveTaxRegime('old');
                  }}
                  className={`min-w-[7.5rem] px-3.5 py-2 text-sm font-medium transition-colors ${
                    selectedTaxRegime === 'old'
                      ? 'bg-redhalo-navy text-white'
                      : 'bg-white text-slate-700 hover:bg-slate-50'
                  } ${(isReadOnly || !taxRegimeChangeAllowed) ? 'cursor-not-allowed opacity-55' : ''}`}
                >
                  Old Regime
                </button>
                <button
                  type="button"
                  disabled={isReadOnly || !taxRegimeChangeAllowed}
                  onClick={() => {
                    if (selectedTaxRegime === 'new' || isReadOnly || !taxRegimeChangeAllowed) return;
                    saveTaxRegime('new');
                  }}
                  className={`min-w-[7.5rem] border-l border-slate-300 px-3.5 py-2 text-sm font-medium transition-colors ${
                    selectedTaxRegime === 'new'
                      ? 'bg-redhalo-navy text-white'
                      : 'bg-white text-slate-700 hover:bg-slate-50'
                  } ${(isReadOnly || !taxRegimeChangeAllowed) ? 'cursor-not-allowed opacity-55' : ''}`}
                >
                  New Regime
                </button>
              </div>
            </div>
            {selectedTaxRegime === 'new' ? (
              <p className="max-w-lg text-xs leading-relaxed text-slate-600 lg:text-right">
                New regime uses standard slabs. Switch to Old Regime to declare HRA, 80C, VIA, and House Property.
              </p>
            ) : (
              <p className="max-w-lg text-xs leading-relaxed text-slate-600 lg:text-right">
                Old regime — complete each section below. Use the info icon for Declaration / Proof window rules.
              </p>
            )}
          </div>
          {!isAdminOrHr && getTaxRegimeLockReason() ? (
            <p className="mt-2 text-xs text-amber-700">{getTaxRegimeLockReason()}</p>
          ) : null}
        </div>

        {/* Section steps (Old regime) — declarations first; Tax liability is the last tab */}
        {selectedTaxRegime === 'old' && (
          <div className="flex flex-shrink-0 flex-col gap-2 border-b border-slate-200 bg-white px-4 py-2.5 sm:px-6">
            <div className="flex items-center justify-between gap-2">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                Sections · {currentSectionIndex + 1}/{tabs.length}
              </p>
              <div className="flex gap-1.5">
                <button
                  type="button"
                  onClick={goToPreviousSection}
                  disabled={currentSectionIndex === 0}
                  className="inline-flex h-7 items-center gap-1 rounded-full border border-slate-300 bg-white px-2.5 text-xs text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  Prev
                </button>
                <button
                  type="button"
                  onClick={goToNextSection}
                  disabled={currentSectionIndex === tabs.length - 1}
                  className="inline-flex h-7 items-center gap-1 rounded-full border border-slate-300 bg-white px-2.5 text-xs text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Next
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            <div className="flex gap-1.5 overflow-x-auto pb-0.5">
              {tabs.map((tab, index) => {
                const active = currentSectionIndex === index;
                const done = savedSections.has(tab.id);
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => goToSection(tab.id)}
                    className={`inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                      active
                        ? 'border-redhalo-navy bg-redhalo-navy text-white'
                        : done
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-900 hover:bg-emerald-100'
                        : 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-white'
                    }`}
                  >
                    <span
                      className={`inline-flex h-4 w-4 items-center justify-center text-[10px] font-bold ${
                        active ? 'text-white/90' : done ? 'text-emerald-700' : 'text-slate-400'
                      }`}
                    >
                      {done && !active ? '✓' : index + 1}
                    </span>
                    {tab.label}
                  </button>
                );
              })}
            </div>
            {getCurrentSection()?.id === 'viaDeductions' && (
              <div className="flex flex-wrap gap-2">
                <div className="border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-800">
                  Total VI-A:{' '}
                  {getViaDeductionsTotal().toLocaleString('en-IN', {
                    maximumFractionDigits: 0,
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Content — full height for declarations; tax illustration lives in-page (new) or last tab (old) */}
        <div className="min-h-0 flex-1 overflow-y-auto bg-[linear-gradient(180deg,#f8fafc_0%,#f1f5f9_100%)] px-4 py-4 sm:px-6">
          <div className="min-h-full rounded-xl border border-slate-200/80 bg-white p-4 shadow-sm sm:p-6">
            {selectedTaxRegime === 'old' ? (
              getCurrentSection()?.content()
            ) : (
              <div className="space-y-6">
                {showPrevEmploymentTab ? renderPreviousEmployment() : null}
                <div className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white px-5 py-5">
                  <h3 className="mb-1 text-base font-semibold text-emerald-900">
                    New Tax Regime Selected
                  </h3>
                  <p className="max-w-2xl text-sm leading-relaxed text-emerald-800/90">
                    Standard deduction and new-regime slabs apply. Investment declarations (HRA, 80C,
                    VIA, House Property) are not used. Switch to Old Regime above to declare those.
                  </p>
                </div>
                {renderTaxLiability()}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-shrink-0 flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-white px-4 py-3 sm:px-6">
          <div className="flex flex-wrap items-center gap-2">
            {historySlot}
            {mode === 'edit' &&
              selectedTaxRegime === 'old' &&
              getCurrentSection()?.id !== 'taxLiability' && (
              <>
                <button
                  type="button"
                  onClick={() => saveCurrentSection()}
                  disabled={isReadOnly}
                  className={`rounded-full px-4 py-2 text-sm font-medium ${
                    isReadOnly
                      ? 'cursor-not-allowed bg-slate-200 text-slate-500'
                      : 'bg-emerald-600 text-white hover:bg-emerald-700'
                  }`}
                >
                  Save and Next
                </button>
                <button
                  type="button"
                  onClick={resetCurrentSection}
                  disabled={isReadOnly}
                  className={`rounded-full border px-4 py-2 text-sm font-medium ${
                    isReadOnly
                      ? 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-500'
                      : 'border-orange-300 bg-orange-50 text-orange-800 hover:bg-orange-100'
                  }`}
                >
                  Reset Section
                </button>
              </>
            )}
            {mode === 'edit' &&
              selectedTaxRegime === 'old' &&
              getCurrentSection()?.id === 'taxLiability' && (
              <button
                type="button"
                onClick={() => setIllustrationRefreshKey((k) => k + 1)}
                className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50"
              >
                Refresh tax illustration
              </button>
            )}
            {mode === 'edit' && selectedTaxRegime === 'new' && showPrevEmploymentTab && (
              <button
                type="button"
                onClick={() => saveCurrentSection('prevEmployment')}
                disabled={isPrevEmploymentReadOnly}
                className={`rounded-full px-4 py-2 text-sm font-medium ${
                  isPrevEmploymentReadOnly
                    ? 'cursor-not-allowed bg-slate-200 text-slate-500'
                    : 'bg-emerald-600 text-white hover:bg-emerald-700'
                }`}
              >
                Save Previous Employment
              </button>
            )}
            {mode === 'edit' && selectedTaxRegime === 'new' && (
              <button
                type="button"
                onClick={() => setIllustrationRefreshKey((k) => k + 1)}
                className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50"
              >
                Refresh tax illustration
              </button>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            {mode === 'edit' &&
              selectedTaxRegime === 'old' &&
              getCurrentSection()?.id === 'taxLiability' && (
                <button
                  type="button"
                  onClick={submitDeclaration}
                  disabled={isReadOnly}
                  className={`rounded-xl px-5 py-2 text-sm font-medium ${
                    isReadOnly
                      ? 'cursor-not-allowed bg-slate-200 text-slate-500'
                      : 'rh-btn-primary hover:opacity-95'
                  }`}
                >
                  Submit Declaration
                </button>
              )}

            {mode === 'edit' && selectedTaxRegime === 'new' && (
              <button
                type="button"
                onClick={submitDeclaration}
                disabled={isReadOnly}
                className={`rounded-xl px-5 py-2 text-sm font-medium ${
                  isReadOnly
                    ? 'cursor-not-allowed bg-slate-200 text-slate-500'
                    : 'rh-btn-primary hover:opacity-95'
                }`}
              >
                Save
              </button>
            )}

            <button
              type="button"
              onClick={handleCancelOrClose}
              className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50"
            >
              {mode === 'view' ? 'Close' : 'Cancel'}
            </button>
          </div>
        </div>

        {showSuccessNotification && (
          <div className="fixed bottom-4 right-4 z-[10060] rounded-xl border border-green-600 bg-green-600 px-4 py-2 text-sm text-white shadow-lg">
            Declaration submitted successfully!
          </div>
        )}
      </div>

      {/* ProofDocumentModal (for uploading documents) */}
      <ProofDocumentModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUpload={handleDocumentUpload}
      />

      {/* Submit Success Modal */}
      <SubmitSuccessModal
        isOpen={showSubmitSuccessModal}
        onClose={handleSubmitSuccessClose}
      />

      {/* Cancel warning when Old Regime (nothing declared) or New Regime selected */}
      {cancelConfirmMode && (
        <div className="fixed inset-0 z-[10060] flex items-center justify-center bg-black/40 p-4">
          <div className="mx-4 w-full max-w-lg rounded-none border border-gray-200 bg-white shadow-lg">
            <div className="border-b border-gray-200 px-5 py-3">
              <h3 className="text-base font-semibold text-gray-900">
                {cancelConfirmMode === 'old_nothing_declared'
                  ? 'Cancel without declaring?'
                  : 'Cancel — New Regime selected'}
              </h3>
            </div>
            <div className="space-y-3 px-5 py-4 text-sm text-gray-700">
              {cancelConfirmMode === 'old_nothing_declared' ? (
                <>
                  <p>
                    You have chosen <span className="font-semibold">Old Regime</span> but not
                    declared anything. It may lead to tax deduction according to the Old tax
                    regime.
                  </p>
                  <p>Do you still want to cancel?</p>
                </>
              ) : (
                <>
                  <p>
                    You have chosen <span className="font-semibold">New Regime</span>.
                  </p>
                  <p>
                    All data (including attachments) saved under the Old Regime will be{' '}
                    <span className="font-semibold">deleted</span> when you proceed.
                  </p>
                  <p>Do you still want to cancel?</p>
                </>
              )}
            </div>
            <div className="flex justify-end gap-2 border-t border-gray-200 bg-gray-50 px-5 py-3">
              <button
                type="button"
                className="rounded-none border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                onClick={() => setCancelConfirmMode(null)}
              >
                No
              </button>
              <button
                type="button"
                className="rounded-none px-4 py-2 text-sm rh-btn-primary hover:opacity-95"
                onClick={() => {
                  setCancelConfirmMode(null);
                  onClose();
                }}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Submit Declaration Confirmation Modal */}
      {submitConfirm.open && (
        <div className="fixed inset-0 z-[10060] flex items-center justify-center bg-black/40 p-4">
          <div className="mx-4 w-full max-w-lg rounded-none border border-gray-200 bg-white shadow-lg">
            <div className="border-b border-gray-200 px-5 py-3">
              <h3 className="text-base font-semibold text-gray-900">
                {submitConfirm.mode === 'old'
                  ? 'Submit Declaration — Old Tax Regime'
                  : 'Submit Declaration — New Tax Regime'}
              </h3>
            </div>
            <div className="space-y-3 px-5 py-4 text-sm text-gray-700">
              {submitConfirm.mode === 'old' ? (
                <>
                  <p>
                    Are you sure you want to submit the investment declaration under the
                    <span className="font-semibold"> Old Tax Regime</span> for this employee?
                  </p>
                  <p>
                    After submission, you may not be able to edit these details if the
                    investment declaration window is locked by your employer.
                  </p>
                </>
              ) : (
                <>
                  <p>
                    You have selected the <span className="font-semibold">New Tax Regime</span>.
                  </p>
                  <p>
                    All data (including attachments) saved under the Old Regime for this
                    employee will be <span className="font-semibold">deleted</span>, except
                    <span className="font-semibold"> Previous Employment</span> details.
                  </p>
                  <p className="text-red-600">
                    This action cannot be undone. Do you wish to proceed and save under the
                    New Regime?
                  </p>
                </>
              )}
            </div>
            <div className="flex justify-end gap-2 border-t border-gray-200 bg-gray-50 px-5 py-3">
              <button
                type="button"
                className="rounded-none border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                onClick={() => setSubmitConfirm({ open: false, mode: null })}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-none px-4 py-2 text-sm rh-btn-primary hover:opacity-95"
                onClick={handleConfirmSubmit}
              >
                Confirm Submit
              </button>
            </div>
          </div>
        </div>
      )}

      <AttachmentPreviewModal
        isOpen={attachmentPreview.isOpen}
        url={attachmentPreview.url}
        downloadUrl={attachmentPreview.downloadUrl}
        fileName={attachmentPreview.fileName}
        onClose={() =>
          setAttachmentPreview({
            isOpen: false,
            url: null,
            downloadUrl: null,
            fileName: '',
          })
        }
      />
    </div>
  );

  return typeof document !== 'undefined'
    ? createPortal(modalNode, document.body)
    : modalNode;
}

// --------------------- Submit Success Modal ---------------------
function SubmitSuccessModal({ isOpen, onClose }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10060] flex items-center justify-center bg-black/50 p-4">
      <div className="relative w-full max-w-md rounded-none border border-gray-200 bg-white shadow-xl">
        <div className="p-6 text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center bg-green-100">
            <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="mb-2 text-lg font-medium text-gray-900">
            Declaration Submitted Successfully!
          </h3>
          <p className="mb-6 text-sm text-gray-600">
            Your tax planning declaration has been submitted and TDS will be computed accordingly.
          </p>
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-none px-4 py-2 rh-btn-primary hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-redhalo-red/35 focus:ring-offset-2"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}

// --------------------- ProofDocumentModal ---------------------
function ProofDocumentModal({ isOpen, onClose, onUpload }) {
  const [selectedDocument, setSelectedDocument] = useState('');
  const [file, setFile] = useState(null);
  const [comment, setComment] = useState('');

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size <= 10 * 1024 * 1024) {
      // 10MB limit
      setFile(selectedFile);
    } else {
      alert('File size should not exceed 10MB');
    }
  };

  const handleUpload = () => {
    if (selectedDocument && file) {
      onUpload({
        documentName: selectedDocument,
        fileName: file.name,
        size: file.size,
        lastUpdated: new Date().toISOString(),
        comment,
      });
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10060] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-2xl rounded-none border border-gray-200 bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold">Upload Document</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4">
          <div className="relative">
            <select
              value={selectedDocument}
              onChange={(e) => setSelectedDocument(e.target.value)}
              className="block px-2.5 pb-2.5 pt-4 w-full text-sm text-gray-900 bg-white rounded-none border-0 border-b border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-b-2 focus:border-redhalo-red hover:border-b-redhalo-navy peer"
            >
              <option value="">Select document type</option>
              <option value="House Rent (HRA)">House Rent (HRA)</option>
              <option value="Section 80C">Section 80C</option>
              <option value="Section 80D">Section 80D</option>
              <option value="Section 80DD">Section 80DD</option>
              <option value="Section 80DDB">Section 80DDB</option>
              <option value="Section 80E">Section 80E</option>
              <option value="Section 80EE">Section 80EE</option>
              <option value="Section 80U">Section 80U</option>
              <option value="Section 80CCD(1B)">Section 80CCD(1B)</option>
              <option value="House Property Documents">House Property Documents</option>
            </select>
            <label
              className="absolute text-sm text-gray-500 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white px-2 rounded-none 
                peer-focus:px-2 peer-focus:text-redhalo-navy 
                peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 
                peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 
                peer-focus:-translate-y-4 left-1"
            >
              Select Document
            </label>
          </div>

          <div className="relative">
            <input
              type="file"
              onChange={handleFileChange}
              className="block px-2.5 pb-2.5 pt-4 w-full text-sm text-gray-900 bg-transparent rounded-none border-0 border-b border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-b-2 focus:border-redhalo-red hover:border-b-redhalo-navy peer"
            />
            <label
              className="absolute text-sm text-gray-500 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white px-2 rounded-none 
                peer-focus:px-2 peer-focus:text-redhalo-navy 
                peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 
                peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 
                peer-focus:-translate-y-4 left-1"
            >
              File
            </label>
            <p className="text-xs text-gray-500 mt-1">Max limit for each file is 10 MB</p>
          </div>

          <div className="relative">
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows="2"
              placeholder=" "
              className="block px-2.5 pb-2.5 pt-4 w-full text-sm text-gray-900 bg-transparent rounded-none border-0 border-b border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-b-2 focus:border-redhalo-red hover:border-b-redhalo-navy peer"
            ></textarea>
            <label
              className="absolute text-sm text-gray-500 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white px-2 rounded-none 
                peer-focus:px-2 peer-focus:text-redhalo-navy 
                peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 
                peer-placeholder-shown:top-1/2 peer-focus:top-2 peer-focus:scale-75 
                peer-focus:-translate-y-4 left-1"
            >
              Comment
            </label>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-none border px-4 py-2 text-sm hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleUpload}
            className="rounded-none bg-redhalo-mint/400 px-4 py-2 text-sm text-white hover:opacity-95"
          >
            Upload
          </button>
        </div>
      </div>
    </div>
  );
}
